import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "./supabaseClient";
import { signIn, signOut as authSignOut, getSession, onAuthChange, ensureFreshSession } from "./auth";
import { fetchProfile, insertTenant, patchProfile, fetchPlatformConfig, ensurePushSubscription, ensureProfileForCaller, recordConsent } from "./api";
import { loadStateForUser, subscribeChanges } from "./dataLoader";
import { syncStateDiff } from "./dataSync";
import { seedTenantDefaults } from "./seedDefaults";
import { loadCachedState, saveCachedState, clearCachedState } from "./stateCache";

// Centralized tuning constants. Tweak here, not at call sites. Keep this
// list short — only put a value here when it appears in MORE THAN ONE place
// or when its meaning isn't obvious from context.
export const CONFIG = Object.freeze({
  // Debounce for setState -> persist. 600ms feels "saved a moment later"
  // without being so long a quick tab-switch loses a write.
  PERSIST_DEBOUNCE_MS: 600,
  // Per-fetch timeouts. Profile is small; full state reload is heavier.
  PROFILE_TIMEOUT_MS: 10_000,
  RELOAD_TIMEOUT_MS: 20_000,
  // Wrong-door auth-source flag freshness. Older than this and we treat
  // the flag as stale rather than blocking the customer login.
  AUTH_SOURCE_FRESHNESS_MS: 60_000,
  // Settle-RPC retry backoff (used by booking submit). 3 attempts × 250ms
  // step keeps the user wait under ~1s in worst case.
  SETTLE_RETRY_ATTEMPTS: 3,
  SETTLE_RETRY_STEP_MS: 250,
  // Default deposit percentage if the shop's policy enables deposits but
  // didn't set a percent. Mirrors PLANS-era default.
  DEFAULT_DEPOSIT_PERCENT: 30,
});

const isCustomerBookingRoute = () => (window.location.hash || "").startsWith("#/book/");

// Wipe the persisted Supabase session + auth markers on native and web. Used
// when a stale / corrupted session keeps failing fetchProfile so the user
// can retry login from a clean slate without manually clearing app data.
// Best-effort: every storage call is wrapped so a single failure (e.g.
// Capacitor Preferences plugin not yet ready) doesn't abort the rest.
async function wipeAuthCache() {
  const KEYS = ["petos-auth", "petos:oauth-pending", "petos:auth-intent", "petos:auth-source", "petos:lastUserId", "petos:view-as", "petos:signup-tenant-slug"];
  try {
    for (const k of KEYS) {
      try { localStorage.removeItem(k); } catch { /* ignore */ }
    }
  } catch { /* ignore */ }
  // Capacitor Preferences mirror — supabaseClient uses this on native.
  try {
    const isCapacitor = typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();
    if (isCapacitor) {
      const { Preferences } = await import("@capacitor/preferences");
      for (const k of KEYS) {
        try { await Preferences.remove({ key: k }); } catch { /* ignore */ }
      }
    }
  } catch { /* ignore */ }
  try { window.__petosAccessToken = null; } catch { /* ignore */ }
}

// One-shot promotion: when the user clicked "Login with Google" from the
// MAIN owner-side Login screen, we set petos:auth-intent=owner-register
// before the OAuth round-trip. After they return, the on-auth-user trigger
// has created a profile with the default role=customer. We promote them to
// owner here so the ShopRegister fallback renders (and they don't get
// dropped into CustomerApp with no shop context). One-shot — flag consumed.
//
// Race-safe: on OAuth return both `init()` and the `onAuthChange` listener
// fire in parallel. Without a lock, the first chain consumes the flag and
// patches role=owner; the second chain reads the (now empty) flag, skips
// the patch, and returns the stale role=customer profile — which then
// overwrites the winning chain's setProfile because it finishes later.
// Cross-tab lock via the Web Locks API. The previous module-scoped
// promotionPromise only protected calls within the SAME tab. If the user
// had two tabs open during the OAuth round-trip, both could read the
// "owner-register" flag and race on patchProfile. navigator.locks gives
// us a true browser-wide exclusive section keyed by name. Falls back to
// a tab-local promise on browsers without the API (very old Safari).
let __tabLocalPromotion = null;
async function withPromotionLock(fn) {
  if (typeof navigator !== "undefined" && navigator.locks && typeof navigator.locks.request === "function") {
    return navigator.locks.request("petos:owner-register-promotion", { mode: "exclusive" }, async () => fn());
  }
  // Fallback: serialize within the tab. Two tabs on this branch can still
  // race, but the affected browser surface (no Web Locks) is minuscule.
  while (__tabLocalPromotion) { try { await __tabLocalPromotion; } catch { /* ignore */ } }
  let release;
  __tabLocalPromotion = new Promise(r => { release = r; });
  try { return await fn(); }
  finally { release(); __tabLocalPromotion = null; }
}

async function applyOwnerRegisterIntent(profile) {
  if (!profile) return profile;
  return withPromotionLock(async () => {
    // Re-read inside the lock — another tab/chain may have already consumed
    // the flag and promoted the profile. Refetch to avoid stale role.
    let intent = null;
    try { intent = localStorage.getItem("petos:auth-intent"); } catch { return profile; }
    if (intent !== "owner-register") {
      try {
        const fresh = await fetchProfile(profile.id);
        return fresh || profile;
      } catch { return profile; }
    }
    try { localStorage.removeItem("petos:auth-intent"); } catch {}
    // Consuming the owner-register intent also clears the wrong-door source
    // marker so it can't linger and falsely block a later customer login.
    try { localStorage.removeItem("petos:auth-source"); } catch {}
    // Never demote an existing owner / superadmin / super user.
    if (profile.tenantId || profile.role === "owner" || profile.role === "superadmin" || profile.isSuper) return profile;
    try {
      const patched = await patchProfile(profile.id, { role: "owner" });
      return patched || { ...profile, role: "owner" };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[PettoBook] failed to promote profile to owner", e);
      return profile;
    }
  });
}

// If an owner finished signUp but their email-confirmation paused the flow
// before insertTenant ran, the ShopRegister form left the pending payload in
// localStorage. After email confirm + login we finish the registration here.
async function completePendingShopIfAny(profile) {
  if (!profile || profile.role !== "owner" || profile.tenantId) return profile;
  let pending;
  try { pending = JSON.parse(localStorage.getItem("petos:pending-shop") || "null"); } catch { pending = null; }
  if (!pending) return profile;
  try {
    const newTenant = await insertTenant({
      name: pending.name, plan: pending.plan, theme: pending.theme,
      ownerId: profile.id, bookingSlug: pending.bookingSlug,
      address: pending.address, phone: pending.phone, dailyLimit: 8,
    });
    // L1: Clear the pending payload AS SOON AS the tenant exists. If we
    // wait until after seed and seed throws, the next login retries
    // insertTenant which fails with a unique-slug constraint and the user
    // is stuck. The tenant is created; seed failures are recoverable in-app.
    try { localStorage.removeItem("petos:pending-shop"); } catch {}
    // Two writes, not one. Linking the profile to the tenant is what makes
    // the shop reachable; permissions are a nicety the owner UI can set
    // later. They used to go in a single UPDATE, so when a guard trigger
    // rejected the permissions change the tenant_id link died with it and
    // the owner landed in an account with no shop. The link now lands on
    // its own and cannot be taken down by the optional half.
    const patched = await patchProfile(profile.id, { tenantId: newTenant.id });
    try {
      await patchProfile(profile.id, {
        permissions: { viewRevenue: true, manageCRM: true, manageSettings: true },
      });
    } catch (permErr) {
      // eslint-disable-next-line no-console
      console.warn("[PettoBook] owner permissions not set at signup", permErr);
    }
    // Record the T&C acceptance captured at signup (email-confirm path — the
    // owner had no session when they accepted, so we log it now that they do).
    if (pending.tosVersion) {
      try { await recordConsent({ tenantId: newTenant.id, purpose: "platform_tos", version: pending.tosVersion, context: { role: "owner", via: "shop_signup_confirmed", accepted_at: pending.tosAcceptedAt || null, shop_slug: pending.bookingSlug } }); } catch { /* non-fatal */ }
    }
    // M2: Surface partial seed failures via a toast so the owner doesn't
    // land in a half-populated dashboard wondering where their services
    // went. Listener on window.__petosToast is registered by ToastContainer.
    const seedRes = await seedTenantDefaults({
      tenantId: newTenant.id,
      tenantSlug: pending.bookingSlug,
      shopName: pending.name,
    });
    if (seedRes?.errors?.length) {
      try {
        if (typeof window !== "undefined" && typeof window.__petosToast === "function") {
          window.__petosToast("部分預設資料未能建立（" + seedRes.errors.length + " 項），請至「服務」/「員工」頁手動補上", "error");
        }
      } catch { /* ignore */ }
    }
    return patched || { ...profile, tenantId: newTenant.id };
  } catch (e) {
    // Leave the payload in localStorage so the user can retry on next login.
    // eslint-disable-next-line no-console
    console.error("[PettoBook] failed to complete pending shop registration", e);
    return profile;
  }
}

// How long a native save may go without a response before we stop waiting and
// ASK THE SERVER what happened. Was 10s, which is most of the stall the owner
// felt: the row was already committed and visible on other devices while the
// chip still said 正在儲存…. Low enough to feel immediate, high enough that a
// normal save (well under a second on a live connection) never reaches it.
const NATIVE_SAVE_VERIFY_MS = 4500;

const EMPTY = {
  tenants: [], users: {}, pets: {}, appointments: [],
  notifications: [], announcements: [], autoNotifications: [],
  platformConfig: {}, reviews: [],
};

// Single source of truth for the whole app.
// Returns { state, setState, profile, loading, error, signIn, signOut, reload }
// ── Did the write ACTUALLY land? Ask the server. ─────────────────────────────
//
// A timed-out save is not a failed save. On native, a write frequently commits
// server-side while the RESPONSE dies on a socket iOS/Android kept in the pool
// across a background — the owner then sees 儲存失敗 on a record that is, in
// fact, saved (they reopen later and it is there). Reporting that as a failure
// is just as wrong as reporting an unsaved record as saved: it pushes the owner
// into re-entering data that already exists, i.e. duplicates.
//
// So before ANY 儲存失敗 caused by a timeout, we read the rows back through the
// hardened raw fetch and compare them with what we meant to write:
//
//   "saved"   — every diffed row is on the server with the intended values.
//   "unknown" — something in the diff cannot be proven either way (a slice with
//               no readback path, a readback that itself failed). The caller
//               keeps its honest failure message. Never guessed at.
//
// There is deliberately no "lost" verdict: absence of proof is reported as
// failure, which is the safe direction.
async function proveNativeWrite(prev, next, startedAt) {
  try {
    const [{ diffArrayById, diffDict }, api] = await Promise.all([
      import("./dataSync.js"),
      import("./api.js"),
    ]);
    const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

    // Slices with no read-back path — if the save touched any of them we can't
    // speak for the whole write, so we say nothing.
    const opaque = ["users", "notifications", "reviews", "announcements", "tenants"];
    for (const k of opaque) {
      const a = prev?.[k], b = next?.[k];
      if (a !== b && !same(a, b)) return "unknown";
    }

    const petD = diffDict(prev?.pets || {}, next?.pets || {});
    if (petD.changed.length) return "unknown";   // pets carry no updated_at
    const aptD = diffArrayById(prev?.appointments || [], next?.appointments || []);

    // Inserts + deletes: existence is the proof.
    const mustExist = [...aptD.added.map(r => r.id)];
    const mustBeGone = [...aptD.removed.map(r => r.id)];
    if (mustExist.length || petD.added.length) {
      const [apts, pets] = await Promise.all([
        mustExist.length ? api.verifyRowsExist("appointments", mustExist) : new Set(),
        petD.added.length ? api.verifyRowsExist("pets", petD.added.map(r => r.id)) : new Set(),
      ]);
      if (!mustExist.every(id => apts.has(id))) return "unknown";
      if (!petD.added.every(r => pets.has(r.id))) return "unknown";
    }
    if (mustBeGone.length || petD.removed.length) {
      const [apts, pets] = await Promise.all([
        mustBeGone.length ? api.verifyRowsExist("appointments", mustBeGone) : new Set(),
        petD.removed.length ? api.verifyRowsExist("pets", petD.removed.map(r => r.id)) : new Set(),
      ]);
      // Deletes are soft (deleted_at) for appointments, so a surviving id is
      // not proof of failure — but it is also not proof of success.
      if (apts.size || pets.size) return "unknown";
    }

    // Edits: compare only the fields this save was actually changing. Anything
    // the database owns (remaining_balance, paid — written by triggers) is left
    // out of the diff by construction, so a mismatch there can't fake a pass.
    if (aptD.changed.length) {
      const ids = aptD.changed.map(r => r.id);
      const server = await api.fetchAppointmentsByIds(ids);
      const byId = new Map(server.map(r => [r.id, r]));
      for (const want of aptD.changed) {
        const got = byId.get(want.id);
        if (!got) return "unknown";
        const before = (prev?.appointments || []).find(x => x.id === want.id) || {};
        for (const key of Object.keys(want)) {
          if (same(before[key], want[key])) continue;   // this save didn't touch it
          if (!same(got[key], want[key])) return "unknown";
        }
      }
    }
    // Nothing was diffed at all, or everything diffed checked out.
    void startedAt;
    return "saved";
  } catch {
    return "unknown";
  }
}

export function useAppState() {
  const [profile, setProfile] = useState(null);
  const [state, setStateRaw] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Sync status surfaced to the UI so owners can see whether their
  // changes have persisted. "idle" — nothing pending; "saving" — a write
  // is in flight; "saved" — last write completed successfully (with
  // `lastSavedAt` timestamp); "error" — last write failed.
  const [syncStatus, setSyncStatus] = useState({ state: "idle", lastSavedAt: null, error: null });
  // Count of in-flight syncs — when multiple setState calls fire in
  // rapid succession we want to stay "saving" until the last one resolves.
  const inflightRef = useRef(0);
  // Phase 4.1 — append a sync attempt to the persisted ring-buffer so
  // the owner-facing 儲存紀錄 panel can show the last 50 attempts +
  // their outcomes. Cheap localStorage write; never throws.
  const recordSyncEvent = (entry) => {
    try {
      if (typeof window === "undefined") return;
      const key = "petos:sync-log";
      const raw = window.localStorage.getItem(key);
      const arr = raw ? JSON.parse(raw) : [];
      arr.unshift({ ...entry, at: Date.now() });
      while (arr.length > 50) arr.pop();
      window.localStorage.setItem(key, JSON.stringify(arr));
      window.dispatchEvent(new CustomEvent("petos:sync-log-changed"));
    } catch { /* ignore — log is best-effort */ }
  };
  // True while a debounced persist is queued but its network write
  // hasn't started yet. `inflightRef` alone misses this window — and
  // during it, a realtime ping for an unrelated table could fire a
  // reload that fetches a pre-write snapshot and wipe the optimistic
  // row (the "new apt flickers in and out" bug). scheduleReload checks
  // both before firing.
  const pendingPersistRef = useRef(false);

  const profileRef = useRef(null);
  // Guards concurrent post-auth bootstraps. onAuthChange can fire multiple
  // times during a single OAuth round-trip (INITIAL_SESSION + SIGNED_IN +
  // TOKEN_REFRESHED). Without this each one races a full reload(p), causing
  // duplicate network work and double-subscribed realtime channels.
  const lastBootedUserIdRef = useRef(null);
  const bootInProgressRef = useRef(false);

  // Track the most-recently committed state for the diff. Using a ref instead
  // of relying on React's updater closure for the prev value, because firing
  // side effects (syncStateDiff) inside `setStateRaw(prev => ...)` is unsafe
  // under React 18 Concurrent rendering — the updater may run twice or be
  // discarded, leading to double-writes or no-writes (which is what we saw
  // in prod: PATCH/POST never reached Supabase).
  const lastCommittedRef = useRef(EMPTY);

  const persist = useCallback((prev, next) => {
    const userId = profileRef.current?.id;
    const startedAt = Date.now();
    inflightRef.current += 1;
    setSyncStatus(s => ({ ...s, state: "saving", error: null }));
    // ── NATIVE dropped-response guard ──────────────────────────────────────
    // After a long background, iOS keeps the WKWebView's HTTP/2 connection in
    // the pool but the underlying socket is dead ("zombie"). A write goes out
    // and COMMITS server-side, but the RESPONSE can't come back — and
    // AbortController.abort() often fails to reject a fetch stuck on that dead
    // socket. So `await upsertAppointment()` inside syncStateDiff never settles,
    // and the only thing that ends persist() is the 15s+25s watchdog below →
    // the "正在儲存…" chip hangs ~40s then shows a FALSE "儲存失敗", even though
    // the row saved at ~1s (confirmed: it shows on other devices).
    //
    // Fix (native only): if the write hasn't confirmed in 10s, treat it as
    // saved — a genuine RLS/validation failure returns a FAST response and
    // settles long before this; only a network-level dropped response hangs,
    // and by then the write has reached the server. Free the write lock, show
    // ✓ 已儲存, and reconnect realtime so the zombie socket that also stopped
    // live updates gets rebuilt. Web is unaffected (keeps the watchdog path).
    let settled = false;
    const finish = () => { if (!settled) { settled = true; inflightRef.current = Math.max(0, inflightRef.current - 1); } };
    const isNativeSave = typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();
    // When a timeout leaves the verdict UNKNOWN we show 儲存失敗 — correct at
    // that moment, but often stale a few seconds later: the write was in
    // flight on a socket that was still coming back, and it lands. Without
    // this the owner is left staring at a failure notice on a record that is
    // now safely on the server (their words: "record is saved after some time,
    // save fail notice still there"). Two spaced re-checks, each a real
    // read-back — the notice is only cleared on proof, never on a timer.
    const recheckLater = (verdictWasUnknown) => {
      if (!isNativeSave || !verdictWasUnknown) return;
      [8000, 25000].forEach((delay) => setTimeout(async () => {
        try {
          if (inflightRef.current > 0) return;         // a newer save owns the status
          if (await proveNativeWrite(prev, next, startedAt) !== "saved") return;
          setSyncStatus(s => (s.state === "error"
            ? { state: "saved", lastSavedAt: Date.now(), error: null }
            : s));
          try { if (typeof window.__petosToast === "function") window.__petosToast("已確認儲存成功 ✓", "success"); } catch { /* ignore */ }
        } catch { /* stay failed — silence is the safe direction */ }
      }, delay));
    };
    // ── Early confirm ────────────────────────────────────────────────────
    // A native write very often COMMITS in well under a second and then loses
    // its response on a socket that died during the background. Nothing used
    // to notice until the 10s watchdog, so the owner sat watching 正在儲存… on
    // a booking other devices had already received.
    //
    // This probe asks the server what it holds and can ONLY report success —
    // it never stashes, never warns, never fails a save. A write that is
    // simply still in flight reads back as "not yet" and is left alone for the
    // watchdog and the retry chain to judge. So it makes the good case fast
    // without making the bad case wrong.
    const nativeEarlyConfirm = isNativeSave ? setTimeout(async () => {
      if (settled) return;
      let verdict = "unknown";
      try { verdict = await proveNativeWrite(prev, next, startedAt); } catch { /* stay quiet */ }
      if (settled || verdict !== "saved") return;
      finish();
      setSyncStatus({ state: "saved", lastSavedAt: Date.now(), error: null });
      recordSyncEvent({ ok: true, error: null, ms: Date.now() - startedAt });
      // eslint-disable-next-line no-console
      console.warn("[PettoBook sync] response not back yet — read-back CONFIRMED the write ✓ (" + (Date.now() - startedAt) + "ms)");
    }, NATIVE_SAVE_VERIFY_MS) : null;
    const nativeSaveWatchdog = isNativeSave ? setTimeout(async () => {
      if (settled) return;
      // VERIFY, don't assume. The old behavior ("hung 10s ⇒ response dropped
      // after commit ⇒ show ✓") was a gamble that lost when the request NEVER
      // LEFT the device (zombie socket right after reopen): the owner added an
      // appointment, saw a false ✓ 已儲存, and it silently vanished on the next
      // cold boot. Now: for ADDED appointments/pets (the provable, high-value
      // case) do a raw read-back on a fresh request — row exists ⇒ genuinely
      // committed; row missing/unverifiable ⇒ STASH to the localStorage outbox
      // (survives process eviction) + show an honest retry status. Replay runs
      // on boot/resume (see outbox.js — existence-checked, resurrection-safe).
      // Edits with no adds keep the old assume-saved path: existence can't
      // prove a change, and edits were never the reported loss.
      // NOTE: still no realtime reconnect here — on native it ties up the
      // WKWebView request queue (wedged "dead on reopen" after a payment).
      let addedRows = [];
      try {
        const { diffArrayById, diffDict } = await import("./dataSync.js");
        const aptAdds = diffArrayById(prev?.appointments || [], next?.appointments || []).added || [];
        const petAdds = diffDict(prev?.pets || {}, next?.pets || {}).added || [];
        addedRows = [
          ...petAdds.map(r => ({ kind: "pet", row: r })),
          ...aptAdds.map(r => ({ kind: "apt", row: r })),
        ];
      } catch { /* diff failure → fall through to assume-saved */ }
      if (settled) return; // real response may have settled while we diffed
      if (!addedRows.length) {
        // Pure EDIT with nothing provable to read back. Do NOT settle here.
        //
        // Settling at 10s looks harmless but it disables the actual recovery:
        // the chain below is runOnce(15s) → warm the socket → runOnce(25s),
        // and BOTH continuations start with `if (settled) return`. Marking the
        // save resolved at 10s therefore skipped the retry — which is the step
        // that usually succeeds, because by then a fresh socket exists. That
        // is why Android still reported 儲存失敗 after a reopen: attempt one
        // died on the dead socket and attempt two never ran.
        //
        // Leaving it unsettled is safe: runOnce races an internal timeout that
        // always rejects, so the chain always reaches finish() (~43s worst
        // case) and reports the TRUE outcome — success, or 儲存失敗 with a
        // retry. Nothing is claimed that wasn't verified, and the lock is
        // always released.
        // eslint-disable-next-line no-console
        console.warn("[PettoBook sync] native: >10s with no adds — letting the retry run (no verdict yet)");
        return;
      }
      let verified = false;
      try {
        const api = await import("./api.js");
        const aptIds = addedRows.filter(r => r.kind === "apt").map(r => r.row.id);
        const petIds = addedRows.filter(r => r.kind === "pet").map(r => r.row.id);
        const verify = (async () => {
          const foundApts = aptIds.length ? await api.verifyRowsExist("appointments", aptIds) : new Set();
          const foundPets = petIds.length ? await api.verifyRowsExist("pets", petIds) : new Set();
          return aptIds.every(id => foundApts.has(id)) && petIds.every(id => foundPets.has(id));
        })();
        verified = await Promise.race([verify, new Promise((res) => setTimeout(() => res(false), 7000))]);
      } catch { verified = false; }
      if (settled) return;
      finish();
      if (verified) {
        setSyncStatus({ state: "saved", lastSavedAt: Date.now(), error: null });
        recordSyncEvent({ ok: true, error: null, ms: Date.now() - startedAt });
        // eslint-disable-next-line no-console
        console.warn("[PettoBook sync] native response dropped — read-back CONFIRMED rows on server ✓");
      } else {
        try {
          const { stashOutbox, replayOutbox } = await import("./outbox.js");
          stashOutbox(addedRows);
          // eslint-disable-next-line no-console
          console.warn("[PettoBook sync] write UNCONFIRMED — stashed " + addedRows.length + " row(s) to outbox for replay");
          // One near-term retry; boot + resume replay cover the rest.
          setTimeout(() => {
            replayOutbox().then(r => {
              if (r.remaining === 0 && r.replayed + r.dropped > 0) {
                setSyncStatus({ state: "saved", lastSavedAt: Date.now(), error: null });
                try { if (typeof window.__petosToast === "function") window.__petosToast("已補存先前未確認的資料 ✓", "success"); } catch { /* ignore */ }
              }
            }).catch(() => { /* next replay on boot/resume */ });
          }, 20000);
        } catch { /* stash is best-effort; status below still warns the user */ }
        setSyncStatus({ state: "error", lastSavedAt: null, error: "尚未確認儲存 — 已保留本地備份，會自動重試" });
        try { if (typeof window.__petosToast === "function") window.__petosToast("⚠️ 儲存未確認 — 已備份，會自動重試（重開 app 亦會補存）", "error"); } catch { /* ignore */ }
        recordSyncEvent({ ok: false, error: "native-unconfirmed-stashed", ms: Date.now() - startedAt });
      }
    }, 10000) : null;
    // Run the sync with a 15s watchdog. If it times out (iOS WebView
    // sometimes silently drops responses after resume), retry once with
    // a fresh 25s watchdog before surfacing failure. The first attempt
    // post-resume often loses the response while the connection pool
    // re-establishes; the second usually succeeds.
    const runOnce = (timeoutMs) => {
      const watchdog = new Promise((_, reject) => setTimeout(() => reject(new Error("sync-timeout-" + Math.round(timeoutMs/1000) + "s")), timeoutMs));
      return Promise.race([syncStateDiff(prev, next, { byUser: userId }), watchdog]);
    };
    runOnce(15_000)
      .catch(async (firstErr) => {
        // Native watchdog already resolved this as saved — don't re-send.
        if (settled) return;
        // Only retry on timeout; if the diff threw a real error, don't
        // double-fire the writes.
        if (!/sync-timeout/.test(String(firstErr?.message || firstErr))) throw firstErr;
        // eslint-disable-next-line no-console
        console.warn("[PettoBook sync] first attempt timed out — retrying once with fresh connection");
        // Tiny ping to warm the connection before the retry. Best-effort.
        //
        // ⚠️ MUST be time-capped (restored — the Aug-04 resume rollback silently
        // dropped this July-27 fix). supabase-js's getSession() can hang forever
        // on a just-woken tab; it sat directly in the retry path, so the 25s
        // retry never started, persist() never settled and the save chip stuck
        // on "正在儲存…". The warm-up is optional; never let it block the retry.
        // getSession() alone is usually a LOCAL storage read — it does not
        // open a connection, so it never healed the black-holed pool it was
        // meant to warm. Hit auth/v1/health as well: a real request forces the
        // stack to evict the dead socket and establish a new one, which is the
        // same trick the native resume path uses. Both are time-capped; the
        // warm-up must never block the retry (that hang was a July regression).
        try {
          const base = (import.meta.env.VITE_SUPABASE_URL || "").replace(/\/$/, "");
          await Promise.race([
            fetch(base + "/auth/v1/health", {
              cache: "no-store",
              headers: { apikey: import.meta.env.VITE_SUPABASE_ANON_KEY || "" },
            }).catch(() => null),
            new Promise((resolve) => setTimeout(resolve, 2500)),
          ]);
        } catch { /* ignore */ }
        try {
          const { supabase } = await import("./supabaseClient.js");
          await Promise.race([
            supabase.auth.getSession(),
            new Promise((resolve) => setTimeout(resolve, 3000)),
          ]);
        } catch { /* ignore */ }
        return runOnce(25_000);
      })
      .then(async (res) => {
        if (nativeSaveWatchdog) clearTimeout(nativeSaveWatchdog);
        if (nativeEarlyConfirm) clearTimeout(nativeEarlyConfirm);
        if (settled) return; // native watchdog already resolved this as saved
        // Same rule as the .catch() below: a per-row net-timeout is "no reply",
        // not "no write". rawWrite gives up at ~12.5s (×2 with its own retry)
        // and the row it was PATCHing has usually already committed — that is
        // the 儲存失敗-on-a-saved-record case. Verify against the server before
        // reporting anything. Errors with a real cause (RLS, validation) are
        // left alone: they are not timeouts and must still be shown.
        if (isNativeSave && res?.errors?.length
            && res.errors.every(er => /net-timeout|sync-timeout/.test(String(er)))) {
          const verdict = await proveNativeWrite(prev, next, startedAt);
          if (settled) return;
          if (verdict === "saved") {
            finish();
            setSyncStatus({ state: "saved", lastSavedAt: Date.now(), error: null });
            recordSyncEvent({ ok: true, error: null, ms: Date.now() - startedAt });
            // eslint-disable-next-line no-console
            console.warn("[PettoBook sync] row write timed out, read-back CONFIRMED it ✓");
            return;
          }
          recheckLater(true);
        }
        // Always decrement inflight, regardless of error/success. Previous
        // code returned early on success without decrementing the error
        // branch's counter consistently, leaving an off-by-one and a stuck
        // "saving" state if multiple syncs raced.
        finish();
        if (res?.errors?.length) {
          // eslint-disable-next-line no-console
          console.error("[PettoBook sync] partial errors:", res.errors);
          // Phase 1.3 — surface ALL errors, not just errors[0]. Dedupe
          // identical messages so the toast doesn't repeat the same RLS
          // denial three times for three rows of the same table.
          // notif-soft tags are downgraded to a quieter "(部分通知未送達)"
          // message because they're best-effort by design.
          const all = res.errors;
          const hard = [...new Set(all.filter(e => !/^\[notif-soft\]/.test(String(e))))];
          const softCount = all.filter(e => /^\[notif-soft\]/.test(String(e))).length;
          const headline = hard.length
            ? (hard.length === 1
                ? "儲存失敗：" + hard[0]
                : "儲存失敗 (" + hard.length + " 項)：" + hard.join("; "))
            : (softCount ? "(部分通知未送達，但資料已儲存)" : "儲存失敗");
          setSyncStatus({ state: hard.length ? "error" : "saved", lastSavedAt: hard.length ? null : Date.now(), error: hard.length ? headline : null });
          try {
            if (typeof window !== "undefined" && typeof window.__petosToast === "function") {
              window.__petosToast(headline, hard.length ? "error" : "info");
            }
          } catch { /* ignore */ }
          recordSyncEvent({ ok: !hard.length, error: hard.length ? headline : null, softCount, ms: Date.now() - startedAt });
        } else if (inflightRef.current <= 0) {
          setSyncStatus({ state: "saved", lastSavedAt: Date.now(), error: null });
          recordSyncEvent({ ok: true, ms: Date.now() - startedAt });
        } else {
          // Another sync is still in flight; just record the success.
          recordSyncEvent({ ok: true, ms: Date.now() - startedAt });
        }
      })
      .catch(async (e) => {
        if (nativeSaveWatchdog) clearTimeout(nativeSaveWatchdog);
        if (nativeEarlyConfirm) clearTimeout(nativeEarlyConfirm);
        if (settled) return; // native watchdog already resolved this as saved
        // eslint-disable-next-line no-console
        console.error("[PettoBook sync] failed:", e);
        // A TIMEOUT is not a verdict — it only means no response came back.
        // On native the request very often committed and lost its reply on a
        // socket that died during the background, which is exactly the
        // "儲存失敗 but the record is there when I reopen" the owner reported.
        // Read the rows back before accusing the save of failing. Only a
        // positive, field-level match counts; anything else falls straight
        // through to the failure path below.
        if (isNativeSave && /sync-timeout/.test(String(e?.message || e))) {
          const verdict = await proveNativeWrite(prev, next, startedAt);
          if (settled) return;
          if (verdict === "saved") {
            finish();
            setSyncStatus({ state: "saved", lastSavedAt: Date.now(), error: null });
            recordSyncEvent({ ok: true, error: null, ms: Date.now() - startedAt });
            // eslint-disable-next-line no-console
            console.warn("[PettoBook sync] response was lost, but read-back CONFIRMED the write ✓");
            return;
          }
          recheckLater(true);
        }
        finish();
        const errMsg = e?.message || String(e);
        setSyncStatus({ state: "error", lastSavedAt: null, error: errMsg });
        try {
          if (typeof window !== "undefined" && typeof window.__petosToast === "function") {
            window.__petosToast("儲存失敗：" + errMsg, "error");
          }
        } catch { /* ignore */ }
        recordSyncEvent({ ok: false, error: errMsg, ms: Date.now() - startedAt });
      });
  }, []);

  // Coalesce rapid setState bursts (typing in shop settings, dragging a
  // slider, etc.) into a SINGLE persist call. The UI still re-renders on
  // every keystroke because setStateRaw fires immediately — only the DB
  // write is debounced. This kills the input-lag the owner saw when
  // editing the shop name / about / WhatsApp template etc. while still
  // saving automatically a beat after they stop typing.
  //
  // Implementation notes:
  //  * snapshotPrev captures the LAST committed snapshot (across multiple
  //    keystrokes) so the diff doesn't lose any field that was modified.
  //  * 600ms idle window matches user expectation of "saved a moment later"
  //    without being so long that a quick tab-switch loses a write.
  //  * Flushed eagerly on unmount + on the existing sync-status "saved"
  //    indicator so the UI shows accurate state.
  const persistTimerRef = useRef(null);
  const persistPendingPrevRef = useRef(null);
  const flushPersist = useCallback(() => {
    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current);
      persistTimerRef.current = null;
    }
    const prev = persistPendingPrevRef.current;
    const next = lastCommittedRef.current;
    persistPendingPrevRef.current = null;
    if (prev && next && prev !== next) {
      pendingPersistRef.current = false;
      persist(prev, next);
    } else {
      pendingPersistRef.current = false;
    }
  }, [persist]);

  const setState = useCallback((updater) => {
    let snapshotPrev, snapshotNext;
    setStateRaw(prev => {
      snapshotPrev = prev;
      snapshotNext = typeof updater === "function" ? updater(prev) : updater;
      return snapshotNext;
    });
    queueMicrotask(() => {
      if (snapshotPrev !== undefined && snapshotNext !== undefined && snapshotPrev !== snapshotNext) {
        // Capture the FIRST prev in the burst so the diff sees the full
        // delta from before-the-burst to the final committed state. Drop
        // intermediate prevs because each keystroke's intermediate state
        // isn't a useful diff target.
        if (persistPendingPrevRef.current == null) {
          persistPendingPrevRef.current = snapshotPrev;
        }
        lastCommittedRef.current = snapshotNext;
        // Mark a write as PENDING from the moment the user-visible change
        // lands locally. Without this, a realtime ping during the 600ms
        // debounce window could trigger a state reload that overwrites the
        // optimistic row with a pre-write server snapshot.
        pendingPersistRef.current = true;
        // Show "saving" right away even though the network call is
        // debounced — owner feedback that work is being captured.
        setSyncStatus(s => ({ ...s, state: "saving", error: null }));
        if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
        persistTimerRef.current = setTimeout(() => {
          persistTimerRef.current = null;
          const prevForSync = persistPendingPrevRef.current;
          const nextForSync = lastCommittedRef.current;
          persistPendingPrevRef.current = null;
          pendingPersistRef.current = false;
          if (prevForSync && nextForSync && prevForSync !== nextForSync) {
            persist(prevForSync, nextForSync);
          }
        }, CONFIG.PERSIST_DEBOUNCE_MS);
      }
    });
  }, [persist]);

  // Flush any pending write on unmount so a quick close + reopen doesn't
  // drop the latest typed value.
  useEffect(() => () => flushPersist(), [flushPersist]);

  // Persist immediately when the page is being hidden (tab switch, navigate
  // away, browser close). The browser may kill the tab before our 600ms
  // timer fires; flushing on visibility change closes that window.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onHide = () => {
      if (document.visibilityState === "hidden") flushPersist();
    };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", flushPersist);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", flushPersist);
    };
  }, [flushPersist]);

  // Re-load when profile changes (login/logout). Uses raw setter so the diff
  // wrapper above does NOT fire on the loaded snapshot.
  // `silent` — skip the loading=true toggle. Used by realtime-triggered
  // refetches so the UI doesn't flash the loader on every save. Only the
  // initial post-login load (where we genuinely have no data yet) sets
  // loading=true so the boot loader stays visible until ready.
  // Resume watchdog — armed on a heavy resume (see the visibility handler).
  // A successful reload disarms it; if it's still armed when the timer fires,
  // recovery wedged and we hard-reload to a known-good state.
  const resumeWatchdogRef = useRef(null);
  const disarmResumeWatchdog = () => {
    if (resumeWatchdogRef.current) { clearTimeout(resumeWatchdogRef.current); resumeWatchdogRef.current = null; }
  };
  const reload = useCallback(async (p, opts = {}) => {
    const silent = opts.silent === true;
    if (!p) { setStateRaw(EMPTY); return; }
    if (!silent) setLoading(true);
    try {
      // Hard 15s cap on the initial load. Safari + ITP occasionally lets one
      // of the parallel Supabase queries hang indefinitely (the rest finish
      // but Promise.all never resolves), leaving the user stuck on the
      // loading screen. If we time out, fall through with empty state — the
      // realtime channel + scheduleReload will populate the UI shortly,
      // which is a far better experience than an infinite spinner.
      const TIMEOUT_MS = 15000;
      let timedOut = false;
      const newState = await Promise.race([
        loadStateForUser(p),
        new Promise(resolve => setTimeout(() => { timedOut = true; resolve(null); }, TIMEOUT_MS)),
      ]);
      if (timedOut) {
        // eslint-disable-next-line no-console
        console.warn("[PettoBook] reload timed out at " + TIMEOUT_MS + "ms — falling through with cached/empty state");
        if (!silent) {
          // Retry once in the background; if it succeeds the UI swaps in.
          loadStateForUser(p).then(s => { if (s) setStateRaw(s); }).catch(() => {});
        }
      } else {
        // Diagnostic: if an owner/staff lands here with an empty tenant
        // list, something blocked the fetch (RLS, schema mismatch, network
        // race). Log loudly so we can see it in the console rather than
        // silently rendering an empty UI. Retry once in case it's a
        // one-off network hiccup.
        const needsTenant = p.role === "owner" || p.role === "staff";
        if (needsTenant && (!newState.tenants || newState.tenants.length === 0)) {
          // eslint-disable-next-line no-console
          console.warn("[PettoBook] reload returned empty tenants[] for", p.role, "tenant_id=" + p.tenantId, "— retrying once");
          try {
            const retry = await loadStateForUser(p);
            if (retry && retry.tenants && retry.tenants.length > 0) {
              setStateRaw(retry);
              setError(null);
              disarmResumeWatchdog(); // healthy load → cancel any pending auto-reload
              try {
                localStorage.setItem("petos:lastUserId", p.id);
                saveCachedState(p.id, { state: retry, profile: p });
              } catch { /* ignore */ }
              return;
            }
          } catch (e2) {
            // eslint-disable-next-line no-console
            console.error("[PettoBook] reload retry also failed:", e2);
          }
          // Both attempts came back empty. Show the empty state — the
          // owner can hit "重新整理資料" in the UI to try again.
          setStateRaw(newState);
          setError(new Error("無法載入店舖資料，請檢查網絡並按下重新整理。"));
          return;
        }
        setStateRaw(newState);
        setError(null);
        disarmResumeWatchdog(); // healthy load → cancel any pending auto-reload
        const safeToCache = !needsTenant || (newState.tenants && newState.tenants.length > 0);
        try {
          localStorage.setItem("petos:lastUserId", p.id);
          if (safeToCache) saveCachedState(p.id, { state: newState, profile: p });
        } catch { /* ignore */ }
      }
    } catch (e) {
      setError(e);
      // eslint-disable-next-line no-console
      console.error("[PettoBook] reload failed", e);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Debounced reload for realtime — coalesces bursts so a single user action
  // (which writes to multiple tables) doesn't trigger N back-to-back refetches.
  const reloadTimerRef = useRef(null);
  // persist() is defined ABOVE scheduleReload but needs to trigger it from the
  // native watchdog. A ref keeps that one-way call safe from declaration order
  // and from capturing a stale closure.
  const scheduleReloadRef = useRef(null);
  const lastReloadAtRef = useRef(0);
  // Realtime ping → debounce a full reload. The old 1500ms felt sluggish:
  // owners complained that "everything needs refresh". 250ms still coalesces
  // back-to-back writes from one user action (pet + appointment) into a
  // single reload but keeps the UI snappy.
  //
  // THROTTLE: also refuse to fire a fresh reload if we just finished one
  // less than 4s ago. On resume, the realtime channel, the heartbeat, and
  // the auth-change handler can all stampede this function. Without the
  // throttle the app spins through 3-4 redundant reloads back-to-back,
  // which is the hang the user reports when switching apps on iPad.
  const scheduleReload = useCallback((p) => {
    if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
    // Race fix: when an outbound write is mid-flight (inflightRef > 0)
    // the optimistic state contains rows that aren't on the server
    // yet. Reloading right now would fetch a pre-write snapshot and
    // wipe those rows — which manifested as "walk-in apt appears,
    // disappears, then reappears" since dataSync re-inserts and
    // realtime fires again. So we poll until the queue drains, then
    // run the normal throttled reload.
    // Hard ceiling on how long we'll defer to an in-flight write. Without it
    // a single write that never settles leaks inflightRef and this loop
    // reschedules itself every 300ms FOREVER — the app silently stops
    // reloading. That is exactly how Android got stuck "always save fail when
    // reopen". Waiting is the right default (it prevents a pre-write snapshot
    // wiping optimistic rows), but not waiting forever.
    const waitStartedAt = Date.now();
    // Above the save chain's worst case (15s + warm-up + 25s ≈ 43s) so this
    // only ever trips on a write that is genuinely stuck, never on one that is
    // still legitimately retrying.
    const MAX_WAIT_FOR_INFLIGHT = 50000;
    const fire = () => {
      // Wait for BOTH: (a) an upsert is mid-network, OR (b) a debounced
      // persist is queued but hasn't started yet. Skipping (b) was the
      // root cause of "new apt appears, disappears, reappears" — the
      // 600ms debounce window let a stale snapshot land between
      // setState and the actual write.
      if (inflightRef.current > 0 || pendingPersistRef.current) {
        if (Date.now() - waitStartedAt < MAX_WAIT_FOR_INFLIGHT) {
          reloadTimerRef.current = setTimeout(fire, 300);
          return;
        }
        // Past the ceiling the write is not coming back. Treat the lock as
        // stale, clear it, and reload — showing server truth beats showing
        // nothing at all.
        // eslint-disable-next-line no-console
        console.warn("[PettoBook sync] reload waited " + MAX_WAIT_FOR_INFLIGHT + "ms on a stuck write — clearing the lock and reloading");
        inflightRef.current = 0;
      }
      lastReloadAtRef.current = Date.now();
      reload(p, { silent: true });
    };
    const sinceLast = Date.now() - (lastReloadAtRef.current || 0);
    const delay = sinceLast < 4000 ? (4000 - sinceLast) : 250;
    reloadTimerRef.current = setTimeout(fire, delay);
  }, [reload]);
  scheduleReloadRef.current = scheduleReload;

  // LIGHT native refetch: ONE scoped appointments query (owner→tenant,
  // customer→own), merged into state. Used by the 45s native poll AND the
  // native resume catch-up — so neither fires the full ~9-query
  // loadStateForUser burst into a cold WKWebView connection (that burst, plus
  // the realtime reconnect, wedged "dead on reopen"). Write-drain guarded.
  // Returns true if the fetch actually completed (connection is ALIVE/warm) —
  // callers use that to decide it's safe to revive realtime + do a full sync.
  const nativeAptRefetch = useCallback(async (p, opts = {}) => {
    if (!p || p.role === "superadmin") return false;
    if (inflightRef.current > 0 || pendingPersistRef.current) {
      // A write is in flight, so we must NOT apply a refetch over it — but
      // "can't refetch right now" is not "the connection is dead". Returning
      // false made the resume loop burn all four attempts and conclude NOT
      // warm (observed: apt-refetch took 0ms, warm=false, ×3), skipping the
      // outbox replay and full sync precisely when a pending write needed the
      // recovery most. Probe the connection instead and report what's true.
      try { const api = await import("./api.js"); return await api.pingRest(p.id); }
      catch { return false; }
    }
    // `withPeople` also pulls profiles + pets so a NEW online booking's customer
    // + pet NAME appear (appointments-only left them blank). Used on RESUME; the
    // 45s poll stays appointments-only (light) to not hammer the WKWebView.
    const withPeople = opts.withPeople === true;
    try {
      const api = await import("./api.js");
      const ninety = (() => { const d = new Date(); d.setDate(d.getDate() - 90); return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0"); })();
      const isStaffSide = !!p.tenantId && (p.role === "owner" || p.role === "staff");
      const jobs = isStaffSide
        ? [ api.fetchTenantAppointments(p.tenantId, { from: ninety }),
            withPeople ? api.fetchTenantProfiles(p.tenantId) : Promise.resolve(null),
            withPeople ? api.fetchTenantPets(p.tenantId) : Promise.resolve(null) ]
        : [ api.fetchCustomerAppointments(p.id, { from: ninety }),
            Promise.resolve(null),
            withPeople ? api.fetchCustomerPets(p.id) : Promise.resolve(null) ];
      const [fresh, profiles, pets] = await Promise.all(jobs);
      if (!Array.isArray(fresh)) return false;
      if (inflightRef.current > 0 || pendingPersistRef.current) return true; // connection warm; skip applying over an in-flight write
      setStateRaw(s => {
        const outOfScope = isStaffSide
          ? s.appointments.filter(a => a.tenantId !== p.tenantId)
          : s.appointments.filter(a => a.customerId !== p.id);
        let users = s.users, petsMap = s.pets;
        if (Array.isArray(profiles) && profiles.length) {
          users = { ...users };
          for (const u of profiles) if (u?.id) users[u.id] = { ...(users[u.id] || {}), ...u, pets: (users[u.id]?.pets || []) };
        }
        if (Array.isArray(pets) && pets.length) {
          petsMap = { ...petsMap };
          if (users === s.users) users = { ...users };
          for (const pet of pets) {
            if (!pet?.id) continue;
            petsMap[pet.id] = { ...(petsMap[pet.id] || {}), ...pet };
            if (pet.ownerId && users[pet.ownerId]) {
              const arr = users[pet.ownerId].pets || [];
              if (!arr.includes(pet.id)) users[pet.ownerId] = { ...users[pet.ownerId], pets: [...arr, pet.id] };
            }
          }
        }
        return { ...s, appointments: [...fresh, ...outOfScope], users, pets: petsMap };
      });
      return true;
    } catch { /* best-effort */ return false; }
  }, []);

  // Realtime channel handle, stored in a ref so we can tear it down +
  // re-create it outside of the auth-bootstrap effect (e.g. when a
  // customer switches active shop and we need to listen to the new
  // tenant's appointments / services).
  const realtimeRef = useRef({ unsub: () => {}, profile: null });
  // Initial: check session, fetch profile, load state
  useEffect(() => {
    let unsubAuth = () => {};
    let unsubRealtime = () => {};

    // Detect whether we're returning from an OAuth round-trip. Two signals:
    //   1. URL hash still has the access_token fragment (Supabase's
    //      detectSessionInUrl is mid-parse — rare since it's synchronous)
    //   2. localStorage "petos:oauth-pending" was set right before we
    //      redirected to Google — survives the redirect and tells us OAuth
    //      is in flight even when Supabase has already stripped the hash.
    // The marker has a 5-minute max age so a stranded flag from an old
    // never-completed flow doesn't trap a fresh visitor in the loader.
    const oauthInUrl = (() => {
      if (typeof window === "undefined") return false;
      const h = window.location.hash || "";
      if (/access_token=|refresh_token=|provider_token=/.test(h)) return true;
      // Native (Capacitor) uses ASWebAuthSession which never navigates
      // the WebView away, so the legacy oauth-pending marker only causes
      // false positives. Discard it unconditionally on native.
      const isNative = !!window.Capacitor?.isNativePlatform?.();
      if (isNative) {
        try { localStorage.removeItem("petos:oauth-pending"); } catch { /* ignore */ }
        return false;
      }
      try {
        const ts = parseInt(localStorage.getItem("petos:oauth-pending") || "0", 10);
        if (ts && Date.now() - ts < 300000) return true;
        if (ts) localStorage.removeItem("petos:oauth-pending"); // stale
      } catch { /* ignore */ }
      return false;
    })();

    let bootAttempt = 0;
    const init = async () => {
      try {
        // Hydrate from IndexedDB cache while the network request is in flight.
        // Lets the UI render the user's last-seen state instantly so reopening
        // the tab after iOS killed the session no longer shows a spinner.
        // The real reload below replaces whatever lands here.
        const cachedUserId = (typeof localStorage !== "undefined" && localStorage.getItem("petos:lastUserId")) || null;
        if (cachedUserId && !oauthInUrl) {
          loadCachedState(cachedUserId).then(cached => {
            if (cached && cached.state && profileRef.current == null) {
              // Sanity-check the cache before hydrating it. A previous
              // session that timed out (loadStateForUser race-loser) can
              // leave behind a snapshot where the user has a role/tenant
              // but the tenants[] array is empty — hydrating that gives
              // the user a blank UI with no spinner and traps them there
              // until they manually reload. Detect the broken shape and
              // skip + clear so the fresh fetch below repopulates from
              // the server.
              const p = cached.profile;
              const needsTenant = p?.role === "owner" || p?.role === "staff";
              const looksBroken = needsTenant && (!cached.state.tenants || cached.state.tenants.length === 0);
              if (looksBroken) {
                // eslint-disable-next-line no-console
                console.warn("[PettoBook] discarding empty cached state for", p?.role, "— forcing fresh fetch");
                try { clearCachedState(cachedUserId); } catch { /* ignore */ }
                return;
              }
              setStateRaw(cached.state);
              if (cached.profile) { profileRef.current = cached.profile; setProfile(cached.profile); }
              setLoading(false);
            }
          }).catch(() => {});
        }
        // Eagerly pull platform_config (RLS allows public SELECT) so pre-login
        // screens like ShopRegister can show the superadmin-configured booking
        // URL prefix / shop domain instead of the hardcoded fallback. Best
        // effort — if it fails, the UI falls back to "pettobook.com".
        fetchPlatformConfig()
          .then(pc => { if (pc?.config) setStateRaw(s => ({ ...s, platformConfig: pc.config, autoNotifications: pc.autoNotifications || [] })); })
          .catch(() => {});

        // Restore the persisted session. We race getSession() against a budget
        // so a genuinely wedged read can't hold the loader hostage — but the
        // budget is GENEROUS (8s, was 3s) on purpose: getSession() resolves the
        // instant the local read completes, so a logged-out visitor still falls
        // through immediately (getSession → null fast). The timeout ONLY bites
        // when the read actually hangs — which on a cold native launch is the
        // first Capacitor Preferences (native-bridge) read while the WebView is
        // still warming. The old 3s cap turned that normal cold-start lag into a
        // wrongful drop-to-login even though the stored token was still valid.
        let session = await Promise.race([
          getSession(),
          new Promise(resolve => setTimeout(() => resolve(null), 8000)),
        ]);
        // Cold-start refresh recovery. On native, supabase-js auto-refresh is
        // OFF, so a fresh launch after the 1-hour access token expired arrives
        // with a stale token — and getSession() either hands that expired token
        // back or wedges trying to refresh it on the cold WKWebView socket
        // (raced out above → null). Either way the OLD code dropped a user with
        // a perfectly valid, weeks-long refresh token straight to the login
        // screen. Mirror the warm-resume path: drive ONE serialized refresh via
        // ensureFreshSession(). It's transient-safe — only a DEFINITIVELY dead
        // refresh token (authInvalid) logs out; a network/timeout blip keeps the
        // user logged in so the 60s sessionPoll / next action can recover.
        if (session?.user) {
          const nowSec = Math.floor(Date.now() / 1000);
          const exp = Number(session.expires_at) || 0;
          if (!exp || exp - nowSec < 60) {
            const r = await ensureFreshSession();
            if (r.ok && r.session?.user) session = r.session;
            else if (r.authInvalid) session = null;
            // transient → keep the (expired) session; fetchProfile may 401 and
            // the sessionPoll recovers rather than ejecting the user now.
          }
        }
        // Capture the cold-boot refresh classification so the null-session
        // fall-through below can distinguish "definitively logged out"
        // (authInvalid → real login screen) from "cold WebView / bridge race
        // / TCP warm-up" (transient → retry, DON'T show login).
        let coldRefreshResult = null;
        if (!session?.user && !oauthInUrl) {
          // getSession came back empty. On native this is the dangerous case:
          // the session lives in Capacitor Preferences (which SURVIVES cold
          // launches), but the localStorage cachedUserId marker does NOT — so
          // we must NOT gate recovery on it. Drive a refresh straight off the
          // persisted refresh token (refreshSession reads Preferences). For a
          // genuinely logged-out visitor this returns authInvalid and we
          // fall through to login — only cost is one extra request.
          const isNative = typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();
          if (isNative || cachedUserId) {
            const r = await ensureFreshSession();
            coldRefreshResult = r;
            if (r.ok && r.session?.user) session = r.session;
          }
        }
        if (session?.user) {
          // Mark boot in progress so the synchronous SIGNED_IN that supabase
          // fires once we subscribe doesn't kick off a second concurrent
          // bootstrap of the same user.
          bootInProgressRef.current = true;
          let p = await (() => { try { window.__petosAccessToken = session.access_token; } catch {} return fetchProfile(session.user.id, session.access_token).then(p => p ? Object.assign(p, { _accessToken: session.access_token }) : p); })();
          p = await applyOwnerRegisterIntent(p);
          p = await completePendingShopIfAny(p);
          if (!p) {
            // Phone-OTP customer flow: profile may not exist yet because
            // link-pets-on-login hasn't run. PublicBooking calls it
            // synchronously after verifyOtp; if we land here without one,
            // it failed — wipe the auth cache so the next login attempt
            // doesn't re-replay the same broken session, then sign out.
            await wipeAuthCache();
            await authSignOut().catch(() => {});
            profileRef.current = null;
            setProfile(null);
            setLoading(false);
            bootInProgressRef.current = false;
            return;
          }
          profileRef.current = p;
          setProfile(p);
          await reload(p);
          lastBootedUserIdRef.current = p.id;
          bootInProgressRef.current = false;
          // Outbox replay — recover any appointment/pet whose write went
          // unconfirmed before the app was killed (see outbox.js). Runs after
          // reload so auth + connection are proven warm. Fire-and-forget;
          // existence-checked inside, so it can never overwrite newer data.
          import("./outbox.js").then(({ outboxCount, replayOutbox }) => {
            if (!outboxCount()) return;
            replayOutbox().then(r => {
              if (r.replayed > 0) {
                try { if (typeof window !== "undefined" && typeof window.__petosToast === "function") window.__petosToast("已補存先前未儲存的資料 ✓ (" + r.replayed + " 項)", "success"); } catch { /* ignore */ }
                scheduleReload(p);
              }
            }).catch(() => { /* retried on resume */ });
          }).catch(() => { /* ignore */ });
          // Successful boot — clear transient auth markers (oauth-pending
          // is consumed by the watchdog; auth-source distinguishes shop-
          // login from /shop/{slug} customer-login).
          try { localStorage.removeItem("petos:oauth-pending"); } catch { /* ignore */ }
          try { localStorage.removeItem("petos:auth-source"); } catch { /* ignore */ }
          unsubRealtime = subscribeChanges({
            profile: p,
            onChange: () => scheduleReload(p),
          });
          realtimeRef.current = { unsub: unsubRealtime, profile: p };
        } else {
          // OAuth callback in flight: Supabase's detectSessionInUrl may still
          // be parsing OR may have failed silently after our redirect. Hold
          // the loader and let onAuthChange (or the OAuth watchdog) take over.
          // We check BOTH the URL hash (in case the fragment is still there)
          // and the localStorage `petos:oauth-pending` marker stamped before
          // the redirect — the marker survives even after Supabase strips
          // the hash, which is the realistic case in production.
          const hash = (typeof window !== "undefined" && window.location.hash) || "";
          const hasOAuthFragment = /access_token=|refresh_token=|provider_token=/.test(hash);
          if (hasOAuthFragment || oauthInUrl) {
            return;
          }
          // Cold-boot refresh-failure guard. Two failure classes:
          //   • transient (network / timeout / cold pool): retry via
          //     bootAttempt loop, never drop to login.
          //   • authInvalid (server said "refresh token gone/used/revoked"):
          //     used to eject immediately. That's the actual regression
          //     source on native — a rotation race between the 50-min
          //     interval and supabase-js's internal state can produce a
          //     bogus "already used" on a token that IS still valid. The
          //     next refresh usually succeeds. So we require TWO
          //     consecutive authInvalid on native before ejecting; single
          //     bad reply just triggers another retry.
          const isNative = typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();
          const refreshWasTransient = coldRefreshResult && !coldRefreshResult.ok && !coldRefreshResult.authInvalid;
          const refreshWasAuthInvalid = coldRefreshResult && coldRefreshResult.authInvalid === true;
          const shouldRetryTransient = (isNative || cachedUserId) && refreshWasTransient && bootAttempt < 15;
          // On native, one authInvalid isn't enough — retry with a delay so
          // the queue clears, then re-check.
          const shouldRetryAuthInvalid = isNative && refreshWasAuthInvalid && bootAttempt === 0;
          if (shouldRetryTransient || shouldRetryAuthInvalid) {
            bootAttempt++;
            bootInProgressRef.current = false;
            // eslint-disable-next-line no-console
            console.warn("[PettoBook] cold-boot refresh " + (refreshWasAuthInvalid ? "authInvalid" : "transient") + " (native=" + isNative + ") — retry #" + bootAttempt + " instead of dropping to login");
            setTimeout(() => { init(); }, refreshWasAuthInvalid ? 2500 : Math.min(1500 * bootAttempt, 8000));
            return;
          }
          setProfile(null);
          setLoading(false);
        }
      } catch (e) {
        // Transient boot failure (very common when the page RELOADS into a
        // still-wedged HTTP/2 connection: fetchProfile / reload throw a
        // network / timeout / 5xx / 401-token-blip). This must NOT drop the
        // user to the login screen — that's the spurious "reload logged me
        // out" regression. The render gate shows a SPINNER while
        // (loading && !profile) — and that spinner already has a manual
        // "太久了？點此重置" escape — so we keep loading=true and RETRY, letting
        // the boot self-heal the moment the connection recovers. We only fall
        // through to setLoading(false) (→ login) for a NON-transient error.
        const msg = String(e?.message || e);
        const looksTransient = /fetchprofile (5\d\d|401|429|0)|failed to fetch|networkerror|load failed|\btimeout\b|aborted|network request failed|the operation was aborted/i.test(msg);
        const isNativeBoot = typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();
        const cachedUid = (typeof localStorage !== "undefined" && localStorage.getItem("petos:lastUserId")) || null;
        const hasSession = !!(cachedUid || isNativeBoot || (typeof window !== "undefined" && window.__petosAccessToken));
        if (looksTransient && hasSession) {
          // A 401 could be a genuinely dead refresh token — resolve that once,
          // and ONLY then (authInvalid) is a drop-to-login correct.
          if (/\b401\b/.test(msg)) {
            try { const r = await ensureFreshSession(); if (r?.authInvalid) { profileRef.current = null; setProfile(null); setLoading(false); return; } } catch { /* transient → retry below */ }
          }
          bootInProgressRef.current = false;
          if (bootAttempt < 15) {
            bootAttempt++;
            // eslint-disable-next-line no-console
            console.warn("[PettoBook] boot transient (" + msg.slice(0, 80) + ") — retry #" + bootAttempt + ", staying on loader (no logout)");
            setTimeout(() => { init(); }, Math.min(1500 * bootAttempt, 8000));
          }
          // else: give up RETRYING but stay on the spinner (loading=true), never
          // login — the manual reset button is the operator's escape hatch.
          return;
        }
        setError(e); setLoading(false);
      }
    };
    init();

    // Safety net: if init/reload hangs, eventually fall back so the user isn't
    // stuck on the loader. BUT — if Supabase's detectSessionInUrl is still
    // grinding through an OAuth fragment, extend the wait. We re-check on each
    // tick: only flip loading=false once the URL no longer has a token in it
    // (Supabase strips it after parsing).
    const SAFETY_TICK_MS = 3000;
    const SAFETY_MAX_NO_OAUTH_MS = 12000; // normal login path
    const SAFETY_MAX_OAUTH_MS = 30000;    // OAuth redirect needs more headroom
    const safetyStart = Date.now();
    const safety = setInterval(() => {
      const hash = (typeof window !== "undefined" && window.location.hash) || "";
      const oauthInFlight = /access_token=|refresh_token=|provider_token=/.test(hash);
      const elapsed = Date.now() - safetyStart;
      const cap = oauthInFlight ? SAFETY_MAX_OAUTH_MS : SAFETY_MAX_NO_OAUTH_MS;
      if (elapsed < cap) return; // still within budget
      clearInterval(safety);
      setLoading(prev => {
        if (prev) console.warn("[PettoBook] load timeout — falling back to login");
        return false;
      });
      // If we hit the hard cap with the token still in the URL, strip it so the
      // user sees a clean marketing/login page rather than a leaking URL bar.
      if (oauthInFlight && typeof window !== "undefined") {
        try { window.history.replaceState({}, "", window.location.pathname); } catch { /* ignore */ }
      }
    }, SAFETY_TICK_MS);

    // Helper: race a promise against a timeout so a hung post-login reload
    // can't trap the user on a blank screen. Throws { code: "timeout" } so
    // the caller can branch on it (sign out, show retry, etc).
    const withTimeout = (promise, ms, label) => Promise.race([
      promise,
      new Promise((_, reject) => setTimeout(
        () => reject(Object.assign(new Error("timeout: " + label), { code: "timeout", label })),
        ms,
      )),
    ]);

    // OAuth-completion watchdog: if the URL had a token fragment but the
    // SIGNED_IN event never arrives (Safari ITP blocked storage, time skew,
    // nonce already consumed by a hot-reload, third-party-cookie issues),
    // we'd otherwise sit on the loader for the full 30s safety cap with no
    // feedback. Fire a 12s timer that surfaces an actionable error instead.
    let oauthWatchdog = null;
    if (oauthInUrl) {
      oauthWatchdog = setTimeout(() => {
        if (!profileRef.current && bootInProgressRef.current === false) {
          // eslint-disable-next-line no-console
          console.warn("[PettoBook] Google OAuth callback didn't produce a session within 12s");
          setError(new Error("Google 登入未完成，請重試或改用 Email 登入。"));
          setLoading(false);
          try { localStorage.removeItem("petos:oauth-pending"); } catch { /* ignore */ }
          if (typeof window !== "undefined") {
            try { window.history.replaceState({}, "", window.location.pathname); } catch { /* ignore */ }
          }
        }
      }, 12000);
    }

    unsubAuth = onAuthChange(async (session) => {
      if (session?.user) {
        // Dedupe: onAuthChange fires INITIAL_SESSION + SIGNED_IN +
        // TOKEN_REFRESHED for the same user during a single OAuth round-trip.
        // Without this guard each fires a full reload(p) concurrently —
        // duplicate network work, doubled realtime subscriptions, and
        // last-write-wins races on setProfile. Skip if we already booted this
        // user and there's no concurrent boot in flight.
        if (lastBootedUserIdRef.current === session.user.id && profileRef.current?.id === session.user.id) {
          return;
        }
        if (bootInProgressRef.current) {
          // Another boot is mid-flight; let it finish.
          return;
        }
        bootInProgressRef.current = true;
        if (oauthWatchdog) { clearTimeout(oauthWatchdog); oauthWatchdog = null; }
        unsubRealtime?.();
        setLoading(true);
        setError(null);
        try {
          // After native ASWebAuthSession dismissal the WebView's network
          // stack can briefly stall — the first fetchProfile occasionally
          // hangs past PROFILE_TIMEOUT_MS even though Supabase is fine.
          // Retry once with a short delay before surfacing the timeout.
          let p;
          try {
            p = await withTimeout((() => { try { window.__petosAccessToken = session.access_token; } catch {} return fetchProfile(session.user.id, session.access_token).then(p => p ? Object.assign(p, { _accessToken: session.access_token }) : p); })(), CONFIG.PROFILE_TIMEOUT_MS, "fetchProfile");
          } catch (e) {
            if (e?.code === "timeout") {
              // eslint-disable-next-line no-console
              console.warn("[PettoBook] fetchProfile timed out, retrying once in 1.5s");
              await new Promise(r => setTimeout(r, 1500));
              p = await withTimeout((() => { try { window.__petosAccessToken = session.access_token; } catch {} return fetchProfile(session.user.id, session.access_token).then(p => p ? Object.assign(p, { _accessToken: session.access_token }) : p); })(), CONFIG.PROFILE_TIMEOUT_MS, "fetchProfile");
            } else { throw e; }
          }
          if (!p) {
            // Two distinct slow-trigger paths converge here:
            //   1. Phone-OTP customer just verified — link-pets-on-login edge
            //      function is creating the profile row asynchronously (~1s).
            //   2. Google OAuth first-time login — the auth.users → profiles
            //      DB trigger has to run; on a cold DB this can take 3-6s.
            // The OAuth path needs a much longer budget. Without it, new
            // Google users get authSignOut()'d here and bounce back to login
            // with "找不到用戶資料" even though they DID successfully sign in
            // with Google. Use a wider retry window when we know we just
            // came back from an OAuth round-trip.
            const maxAttempts = oauthInUrl ? 8 : 3;
            const baseDelay = oauthInUrl ? 800 : 600;
            for (let i = 0; i < maxAttempts && !p; i++) {
              // Mild exponential backoff so we're not hammering a slow trigger.
              await new Promise(r => setTimeout(r, baseDelay + i * 250));
              try { p = await (() => { try { window.__petosAccessToken = session.access_token; } catch {} return fetchProfile(session.user.id, session.access_token).then(p => p ? Object.assign(p, { _accessToken: session.access_token }) : p); })(); } catch { /* ignore */ }
            }
          }
          if (!p) {
            // Self-heal: an authenticated user with NO profile row means
            // the on_auth_user_created trigger didn't fire (or the
            // profile was later deleted while auth.users stayed). Call
            // the SECURITY DEFINER ensure_profile_for_caller RPC to
            // recreate a customer-default row keyed by auth.uid(). Only
            // when this also fails do we give up and sign them out.
            try {
              p = await ensureProfileForCaller();
              // eslint-disable-next-line no-console
              if (p) console.warn("[PettoBook] self-healed missing profile for", session.user.id);
            } catch (healErr) {
              // eslint-disable-next-line no-console
              console.error("[PettoBook] ensure_profile_for_caller failed:", healErr);
            }
          }
          if (!p) {
            // Self-recovery: a session exists in storage but every
            // fetchProfile (incl. retries + ensure_profile RPC) failed.
            // Most common cause is a stale token cached from a previous
            // install. Wipe the auth cache so the next OAuth attempt
            // starts from scratch instead of replaying the bad token.
            await wipeAuthCache();
            await authSignOut().catch(() => {});
            profileRef.current = null;
            setProfile(null);
            setError(new Error(oauthInUrl
              ? "Google 登入成功但用戶資料未建立，請稍候再試。"
              : "登入失敗：找不到用戶資料，請重試。"));
            setLoading(false);
            bootInProgressRef.current = false;
            return;
          }
          // Promotion + wrong-door guard run AFTER the profile actually
          // exists. New Google/OTP users are created lazily as a
          // customer-default row by the retry loop / ensure_profile RPC
          // ABOVE, so doing this on the first (often null) fetch missed
          // them — leaving a tenant-less customer on the homepage instead
          // of routing to owner registration.
          p = await applyOwnerRegisterIntent(p);
          p = await completePendingShopIfAny(p);
          // Customer logged in via the owner-side door (intentionally via
          // Google button) — let them through to CustomerApp instead of
          // signing them out. They've explicitly OAuth'd, they have an
          // existing tenant, so route them to their shop's customer view.
          // The old wrong-door block was overzealous and stranded real
          // customers on the login screen.
          if (p && p.role === "customer" && p.tenantId) {
            try { localStorage.removeItem("petos:auth-source"); } catch { /* ignore */ }
          }
          profileRef.current = p;
          setProfile(p);
          // RevenueCat identify — best-effort, never block the boot path.
          // Server-side webhook is authoritative for entitlement; this
          // call just hooks the SDK so the user can later purchase / restore.
          try {
            const { initRevenueCat } = await import("./revenuecat.js");
            initRevenueCat(p.id).catch(() => { /* swallow — purchases optional */ });
          } catch { /* ignore */ }
          // Customer just signed up at /shop/{slug} and confirmed their
          // email? Make sure they have a membership at that shop so the
          // first booking attempt doesn't land them in a no-tenant void.
          // The slug was stashed at signUp time; we clear it once the
          // membership is ensured so it doesn't fire on every login.
          try {
            const slug = (typeof localStorage !== "undefined") ? localStorage.getItem("petos:signup-tenant-slug") : null;
            if (slug && p.role === "customer") {
              const { upsertCustomerMembership } = await import("./api.js");
              const { supabase } = await import("./supabaseClient.js");
              const { data: trow } = await supabase.from("tenants").select("id").eq("booking_slug", slug).maybeSingle();
              if (trow?.id) {
                await upsertCustomerMembership({ userId: p.id, tenantId: trow.id, displayName: p.name || "", phone: p.phone || "" }).catch(() => {});
              }
              try { localStorage.removeItem("petos:signup-tenant-slug"); } catch { /* ignore */ }
            }
          } catch (memErr) { console.warn("[PettoBook] post-confirm membership ensure failed:", memErr); }
          // Cap initial post-login reload at 20s. If it hangs (slow network,
          // RLS-heavy query, huge tenant payload) we surface an error instead
          // of leaving the user on a frozen blank screen — they can retry or
          // sign out from the error banner.
          try {
            await withTimeout(reload(p), CONFIG.RELOAD_TIMEOUT_MS, "reload");
          } catch (e) {
            if (e?.code === "timeout") {
              // eslint-disable-next-line no-console
              console.warn("[PettoBook] post-login reload timed out — keeping session, user can retry");
              setError(new Error("資料載入超時，請檢查網絡並重試。"));
            } else {
              throw e;
            }
          }
          setLoading(false);
          lastBootedUserIdRef.current = p.id;
          // Clear the OAuth-pending marker — we successfully booted.
          try { localStorage.removeItem("petos:oauth-pending"); } catch { /* ignore */ }
          unsubRealtime = subscribeChanges({
            profile: p,
            onChange: () => scheduleReload(p),
          });
          realtimeRef.current = { unsub: unsubRealtime, profile: p };
          // Best-effort web-push registration so the user gets OS-level alerts
          // even when the browser is closed. Caller (signIn) was a user gesture,
          // so the permission prompt is allowed to fire here.
          try {
            const cfg = await fetchPlatformConfig();
            const vapid = cfg?.config?.vapidPublicKey;
            if (vapid) await ensurePushSubscription({ userId: p.id, vapidPublicKey: vapid });
          } catch { /* ignore — push is best-effort */ }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error("[PettoBook] login flow failed:", e);
          if (e?.code === "timeout" && e?.label === "fetchProfile") {
            // Profile fetch itself hung — likely network OR a stale
            // session whose token Supabase refuses to validate. Wipe the
            // local auth cache so the retry uses a clean slate; without
            // this the user has to manually clear app data to recover.
            await wipeAuthCache();
            await authSignOut().catch(() => {});
            profileRef.current = null;
            setProfile(null);
            setError(new Error("登入逾時，請檢查網絡並重試。"));
          } else {
            setError(e instanceof Error ? e : new Error(String(e?.message || e)));
          }
          setLoading(false);
        } finally {
          bootInProgressRef.current = false;
        }
      } else {
        lastBootedUserIdRef.current = null;
        // Wipe transient auth markers on signout so a stale flag from a
        // shop-login OAuth attempt can't trip the customer-reject guard
        // on a later /shop/{slug} login.
        try { localStorage.removeItem("petos:auth-source"); } catch { /* ignore */ }
        profileRef.current = null;
        setProfile(null);
        setStateRaw(EMPTY);
        setError(null);
        setLoading(false);
      }
    });

    // Global session-expired recovery. When ANY raw fetch in api.js / a
    // realtime poll / a supabase-js call returns 401 with an invalid-JWT
    // payload, the app dispatches `petos:session-expired` on window.
    // We force-clear local profile here so the user lands back on the
    // login screen instead of a frozen role view with dead buttons.
    // Also: poll the live session every 60s while visible — if supabase-js
    // silently lost it (refresh_token expired), surface the same recovery.
    const forceLocalLogout = async (reason) => {
      // eslint-disable-next-line no-console
      console.warn("[PettoBook] session expired — forcing local logout:", reason || "");
      lastBootedUserIdRef.current = null;
      profileRef.current = null;
      try { window.__petosAccessToken = null; } catch { /* ignore */ }
      try { await authSignOut(); } catch { /* already-dead session */ }
      setProfile(null);
      setStateRaw(EMPTY);
      setError(null);
      setLoading(false);
      // Phase 2.6 — contextual logout message per reason. Today's "Session
      // expired — please sign in again" was identical regardless of WHY
      // (forced re-auth from 401, long iOS backgrounding, refresh token
      // gone, etc). A friendlier per-cause message helps the user not
      // panic.
      const r = String(reason || "401");
      const msg =
        /long-bg-no-session/.test(r) ? "已超過 2 小時冇用，請重新登入"
        : /long-bg-refresh-failed|long-bg-threw/.test(r) ? "Session 已過期，請重新登入"
        : /resume-refresh-failed|resume-refresh-threw/.test(r) ? "Session 已過期，請重新登入"
        : /refresh-failed/.test(r) ? "請重新登入"
        : /401|403/.test(r) ? "已被登出，請重新登入"
        : "Session 已過期，請重新登入";
      try {
        if (typeof window !== "undefined" && window.__petosToast) {
          window.__petosToast(msg, "warning");
        }
      } catch { /* ignore */ }
    };
    // Native second-chance guards. supabase-js's token rotation racing with
    // the 50-min interval can produce a bogus "already used" authInvalid
    // even when the refresh token IS still valid; the very next refresh
    // succeeds. Require TWO consecutive authInvalid results before ejecting
    // on Capacitor. `authInvalidStreakRef` resets on any successful refresh.
    const authInvalidStreakRef = { current: 0 };
    const isNativePlatform = () => typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();
    const ejectOnAuthInvalid = async (reason) => {
      if (isNativePlatform()) {
        authInvalidStreakRef.current += 1;
        if (authInvalidStreakRef.current < 2) {
          // eslint-disable-next-line no-console
          console.warn("[PettoBook] native: 1st authInvalid — waiting for 2nd before ejecting", reason);
          // Try once more after a beat; if it now succeeds, streak resets.
          setTimeout(async () => {
            try { const r2 = await ensureFreshSession(); if (r2?.ok) authInvalidStreakRef.current = 0; else if (r2?.authInvalid) forceLocalLogout(reason + "-2nd"); } catch { /* ignore */ }
          }, 3000);
          return;
        }
      }
      forceLocalLogout(reason);
    };

    // A 401 usually just means the access token expired a moment before a
    // request fired. Don't log out reflexively — attempt ONE serialized refresh
    // and only eject if the refresh token itself is dead. Transient/network →
    // stay logged in; the user's next action retries with the refreshed token.
    const onSessionExpired = async (e) => {
      if (!profileRef.current) return;
      try {
        const r = await ensureFreshSession();
        if (r.ok) { authInvalidStreakRef.current = 0; return; }
        if (r.authInvalid) await ejectOnAuthInvalid(e?.detail?.reason || "401-auth-invalid");
        // transient → do nothing; keep the session.
      } catch { /* keep the session */ }
    };
    if (typeof window !== "undefined") {
      window.addEventListener("petos:session-expired", onSessionExpired);
    }
    // Proactive session validity poll. The prior version only fired when
    // supabase-js had NO session object — but the common zombie state is
    // "session object present, access_token already expired, auto-refresh
    // silently failed." Detect that here by reading session.expires_at
    // (seconds since epoch) and force-refreshing once before giving up.
    const sessionPoll = setInterval(async () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      if (!profileRef.current) return;
      try {
        const session = await getSession();
        if (!session?.user) {
          // Missing local session could be a transient read race — verify with
          // a real refresh before ejecting. Only log out if the token is dead.
          const r = await ensureFreshSession();
          if (r.ok) authInvalidStreakRef.current = 0;
          else if (r.authInvalid) await ejectOnAuthInvalid("session-poll-invalid");
          return;
        }
        const nowSec = Math.floor(Date.now() / 1000);
        const exp = Number(session.expires_at) || 0;
        // Refresh proactively when within 5 min of expiry. Wide headroom means
        // the token is virtually always fresh, so requests never hit a 401 in
        // the first place. Serialized + transient-safe: only log out if the
        // refresh token is genuinely invalid; a network blip waits for next tick.
        if (exp && exp - nowSec < 300) {
          const r = await ensureFreshSession();
          if (r.ok) authInvalidStreakRef.current = 0;
          else if (r.authInvalid) await ejectOnAuthInvalid("refresh-invalid");
        }
      } catch { /* network blip — leave for next tick */ }
    }, 60_000);

    // When the tab regains focus after being backgrounded, browsers throttle
    // WebSockets (and may close them entirely on mobile / iOS Safari), so the
    // realtime channel can miss notifications inserted while we were away.
    // We track HOW long we were hidden — a 2-second app-switch doesn't
    // need a full reload (the channel survives that), but a 30-second
    // backgrounding or a discarded tab does.
    let hiddenSinceTs = 0;
    // Sequence for the native resume warm-retry loop: each resume bumps it, so
    // an older loop still mid-retry silently yields to the newest resume.
    let nativeResumeSeq = 0;
    // Lowered from 15s → 5s: iOS WebView's request pool degrades fast
    // when backgrounded. Anything over ~5s benefits from a full reload
    // path (session refresh + realtime reconnect + data refetch) so the
    // app comes back to a known-good state instead of a half-broken one
    // where edits silently fail to persist.
    // Threshold above which a resume runs recovery. We no longer reload the
    // page just because this elapsed — a reload would wipe an in-progress form.
    // Instead we probe the connection (below) and only reload if it's truly
    // dead and the user isn't mid-form. Keeps the app alive without nuking work.
    const RESUME_RELOAD_THRESHOLD_MS = 5_000;
    // `forceVisible === true` (ONLY the native appStateChange("active") path
    // passes it — the DOM listener passes an Event object here, hence the
    // strict check) bypasses the document.visibilityState read. On iOS reopen
    // WKWebView often still reports "hidden" at the moment Capacitor fires
    // "active", so trusting visibilityState misread RESUME as GOING-HIDDEN:
    // it restamped hiddenSinceTs (wiping the real gap) and returned without
    // any recovery — the app sat frozen/stale until the user's first tap
    // finally fired a focus event with visibilityState === "visible". That
    // was the owner's "reopen → dead until I click something" bug.
    const onVisibilityChange = (forceVisible) => {
      if (typeof document === "undefined") return;
      const p = profileRef.current;
      if (!p) return;
      if (forceVisible !== true && document.visibilityState === "hidden") {
        // Page going away — flush any pending writes and remember when.
        try { flushPersist(); } catch { /* ignore */ }
        hiddenSinceTs = Date.now();
        // Persist the hidden timestamp. sessionStorage AND localStorage:
        // Safari's tab-discard wipes sessionStorage, so localStorage is the
        // real source of truth for "how long was the PWA suspended." Without
        // this, iPhone PWA came back from a long idle with hiddenFor=0 and
        // skipped the long-gap recovery, leaving the user on a zombie session.
        try { sessionStorage.setItem("petos:hiddenAt", String(hiddenSinceTs)); } catch { /* ignore */ }
        try { localStorage.setItem("petos:hiddenAt", String(hiddenSinceTs)); } catch { /* ignore */ }
        return;
      }
      // visibilityState === "visible"
      let hiddenFor = hiddenSinceTs ? (Date.now() - hiddenSinceTs) : 0;
      if (!hiddenFor) {
        // In-memory stamp missing (bfcache / Page-Lifecycle freeze restore that
        // skipped the hidden branch above) — recover the gap from the persisted
        // stamps. Prefer localStorage (survives Safari tab-discard) and fall
        // back to sessionStorage.
        try {
          const l = Number(localStorage.getItem("petos:hiddenAt")) || 0;
          const s = Number(sessionStorage.getItem("petos:hiddenAt")) || 0;
          const best = Math.max(l, s);
          if (best) hiddenFor = Date.now() - best;
        } catch { /* ignore */ }
      }
      hiddenSinceTs = 0;
      try { sessionStorage.removeItem("petos:hiddenAt"); } catch { /* ignore */ }
      try { localStorage.removeItem("petos:hiddenAt"); } catch { /* ignore */ }
      // Connection warm-up: fire a tiny getSession() right on resume to
      // wake the WebView's request pool. Without this, the FIRST edit
      // after the user comes back can hang for 30s while the pool
      // re-establishes a TCP/TLS connection (the 30s watchdog then fires
      // even though nothing is actually broken). Best-effort — never
      // awaited, never throws.
      if (hiddenFor > 1000) {
        (async () => {
          try {
            const { supabase } = await import("./supabaseClient.js");
            await supabase.auth.getSession();
          } catch { /* ignore */ }
        })();
      }
      // Frozen / discarded check (Chromium + iOS 16+). When true, the JS
      // heap was thrown out — we definitely need to refetch.
      const wasDiscarded = (typeof document !== "undefined" && document.wasDiscarded === true);
      const editing = () => (typeof window !== "undefined" && window.__petosEditingForm === true);
      const isNativeResume = typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();
      // Long-sleep recovery (WEB / PWA only): after a long gap the WebView is
      // often a zombie — session says "logged in" but the WebSocket is dead,
      // the HTTP/2 pool is dead, the next write silently 401s or hangs. Users
      // think they saved an appointment update; the shop never gets it.
      //
      // Previous approach: reload the page. Problem: the reload sometimes
      // boots into the SAME zombie state (session restored from storage, all
      // the same sockets), so the user still can't do anything and the write
      // still fails silently.
      //
      // New approach: on WEB / PWA (not native), just log the user out and
      // reload. Reopen = clean login = definitely fresh session, sockets,
      // pool. Losing a re-login is nothing compared to losing a booking they
      // thought they saved. Native (Capacitor) keeps long-lived login because
      // the app can't be casually reopened and the user pays a real cost for
      // re-auth.
      // 8 hours, not 20 minutes.
      //
      // 20 minutes made this fire on an ordinary working day: a groom, a lunch
      // break, a school run — each one cost the owner a re-login, with no
      // warning and no way back to where they were. Measured on 2026-09-21:
      // hidden 25 min → forced logout → login screen.
      //
      // The rule was written when a long-slept web page was genuinely
      // dangerous: the session looked valid, the sockets were dead, and the
      // next write silently 401'd or hung, so the shop lost a booking it
      // thought it had saved. That danger is now DETECTED rather than assumed
      // — pgRest refreshes and retries on a 401, ensureFreshSession can no
      // longer wedge, and persist() verifies a write by reading it back before
      // reporting either success or failure. A zombie no longer loses data
      // silently, so the blanket logout no longer has to be hair-trigger.
      //
      // It is kept, not deleted, as a backstop for a genuinely abandoned tab:
      // below this threshold the resume still probes the connection and only
      // reloads when it is actually dead.
      const IDLE_LOGOUT_MS = 8 * 60 * 60 * 1000;
      if (!isNativeResume && hiddenFor > IDLE_LOGOUT_MS) {
        try { flushPersist(); } catch { /* ignore */ }
        // eslint-disable-next-line no-console
        console.warn("[PettoBook] web idle > " + Math.round(hiddenFor / 60000) + "min — force logout + reload (avoid zombie session)");
        (async () => {
          try { await authSignOut(); } catch { /* ignore */ }
          try { window.__petosAccessToken = null; } catch { /* ignore */ }
          try { localStorage.removeItem("petos-auth"); } catch { /* ignore */ }
          try { window.location.replace(window.location.pathname + window.location.search); }
          catch { try { window.location.reload(); } catch { /* ignore */ } }
        })();
        return;
      }
      // Shared hard-reload routine — only used when the connection is genuinely
      // dead (or the heap was discarded). Refreshes the token, then reloads to a
      // known-good boot. Walk-in drafts are persisted to localStorage.
      const hardReloadToRecover = () => {
        try { unsubRealtime?.reconnect?.(); } catch { /* ignore */ }
        disarmResumeWatchdog();
        resumeWatchdogRef.current = setTimeout(() => {
          resumeWatchdogRef.current = null;
          try { flushPersist(); } catch { /* ignore */ }
          // eslint-disable-next-line no-console
          console.warn("[PettoBook] resume watchdog fired — auto-reloading to recover");
          try { if (typeof window !== "undefined") window.location.reload(); } catch { /* ignore */ }
        }, 12000);
        (async () => {
          const r = await ensureFreshSession();
          // Only eject on a genuinely dead refresh token. Transient/network →
          // reload anyway (the reboot retries with whatever token we have).
          if (r.ok) authInvalidStreakRef.current = 0;
          else if (r.authInvalid) { disarmResumeWatchdog(); await ejectOnAuthInvalid("resume-refresh-invalid"); return; }
          scheduleReload(p);
        })();
      };

      if (wasDiscarded) {
        if (isNativeResume) {
          // NATIVE: never auto-reload — a WKWebView reload wedges the request
          // queue AND wipes unsaved in-progress work (grooming photo uploads,
          // reports, status changes) that the editing() flag doesn't cover.
          // Soft recovery only: reconnect realtime + silent refetch. If the
          // connection is genuinely dead the user force-quits (their stated
          // preference), which costs nothing vs. losing input.
          try { unsubRealtime?.reconnect?.(); } catch { /* ignore */ }
          try { scheduleReload(p); } catch { /* ignore */ }
        } else {
          // Heap was thrown out — there's no live state to preserve, so reload.
          hardReloadToRecover();
        }
      } else if (hiddenFor > RESUME_RELOAD_THRESHOLD_MS) {
        // Non-destructive recovery: reconnect realtime, then (WEB only) refetch.
        //
        // The refetch is the fix for the web "reopen the tab shows old data
        // until a manual refresh" bug: Supabase realtime does NOT replay
        // postgres_changes that fired while the tab was hidden, so a healthy
        // reconnect alone stays stale. scheduleReload is silent + write-drain-
        // guarded and never wipes a form.
        //
        // ⚠️ NATIVE IS EXCLUDED. On the iOS WKWebView an extra full reload here
        // piles onto an already-fragile request queue and helped wedge the app
        // ("cannot connect / cannot save"). Native resume keeps the original,
        // lighter behavior: reconnect realtime + probe, and only reload if the
        // connection is genuinely dead (handled below).
        if (!isNativeResume) {
          // WEB: reconnect realtime (instant updates) + full silent refetch.
          try { unsubRealtime?.reconnect?.(); } catch { /* ignore */ }
          scheduleReload(p);
        } else {
          // NATIVE — WARM-FIRST recovery (full sync + instant realtime, no wedge).
          // The old "dead on reopen" wedge was the realtime reconnect RETRY-LOOP
          // colliding with a full query burst on a still-COLD WKWebView connection.
          // Fix the ORDER instead of dropping features:
          //   1) a LIGHT refetch first — warms the connection + names new bookings,
          //      and tells us whether the connection is actually alive.
          //   2) ONLY if alive: a full silent refetch (services/memberships/
          //      packages/etc.) for FULL sync, plus a fresh-token realtime reconnect
          //      for INSTANT updates. On a warm connection the socket connects
          //      first-try (no retry loop) and the burst doesn't hit a cold queue.
          //   3) If NOT alive, RETRY with a fresh-socket warm-up (July-parity
          //      auto-update on reopen, WITHOUT July's location.reload() — that
          //      reload rebuilt the network stack but ate unsaved input, and the
          //      owner's standing directive is no reload + no data loss). The
          //      old single-shot `if (!warm) return` left a cold reopen stale
          //      until the 45s poll — the "reopen shows nothing until later /
          //      until I tap" complaint. Each warm-up hits auth/v1/health with
          //      cache:no-store, forcing a NEW TCP/TLS setup that revives a
          //      black-holed HTTP/2 pool (same trick rawWrite now uses).
          const mySeq = ++nativeResumeSeq;
          (async () => {
            // Resume timing, permanently instrumented. "How long after reopen
            // is the app actually showing current data?" is the question the
            // owner feels every single time they open the app, and it used to
            // be unanswerable — we could only guess from behaviour. These three
            // marks (t0 → warm → fully synced) show up in logcat/Safari console
            // and make a regression here measurable instead of anecdotal.
            const t0 = Date.now();
            const since = () => (Date.now() - t0) + "ms";
            // Tell the raw fetch layer the connection is not to be trusted for
            // the next minute, so the owner's FIRST save after reopening gives
            // up on a dead socket in ~3.5s instead of 12.5s. Cleared by the
            // first request that actually comes back.
            import("./api.js").then(m => m.markSocketSuspect?.()).catch(() => {});
            // eslint-disable-next-line no-console
            console.warn("[PettoBook resume] t0 — hidden for " + Math.round(hiddenFor / 1000) + "s");
            const wait = (ms) => new Promise(res => setTimeout(res, ms));
            const warmSocket = async () => {
              const w0 = Date.now();
              try {
                await Promise.race([
                  fetch((import.meta.env.VITE_SUPABASE_URL || "").replace(/\/$/, "") + "/auth/v1/health",
                    { cache: "no-store", headers: { apikey: import.meta.env.VITE_SUPABASE_ANON_KEY || "" } }),
                  wait(2500),
                ]);
              } catch { /* handshake attempt itself still warms; retry regardless */ }
              // eslint-disable-next-line no-console
              console.warn("[PettoBook resume] warm-socket took " + (Date.now() - w0) + "ms");
            };
            const pauses = [250, 1250, 2500, 4000]; // attempts land ~0.25s / 1.5s / 4s / 8s after reopen
            let warm = false;
            for (let i = 0; i < pauses.length; i++) {
              await wait(pauses[i]);
              if (mySeq !== nativeResumeSeq) { console.warn("[PettoBook resume] cancelled at pause " + i + " (superseded)"); return; }   // a newer resume owns recovery now
              // Warm on EVERY attempt — but on the FIRST one, do not WAIT for it.
              //
              // Warming before the first read costs ~2s of dead time on a
              // connection that is usually fine (measured on Android: 2.0s of
              // a 4.6s resume). Firing it without awaiting still does the job
              // it was added for — it heals a black-holed pool early, so a
              // write left pending from before the background has a live
              // socket to retry onto — without making every healthy reopen
              // pay for it. From the second attempt on we KNOW the socket is
              // bad, so there we wait for the warm-up before retrying.
              // Warm on EVERY attempt including the first.
              //
              // This used to start at i > 0, so the socket was only rebuilt
              // after the first refetch had already failed. That ordering is
              // backwards for the case that matters most: the owner edits
              // something, backgrounds the app, the write is left hanging on a
              // socket that dies, and they reopen. The pending write then
              // retries against a pool nobody has healed yet. Warming first is
              // a plain GET to auth/v1/health — it touches no app state, so it
              // is safe to do even with a write in flight, and it gives that
              // write's retry a live socket to land on.
              if (i === 0) { warmSocket(); }
              else { await warmSocket(); if (mySeq !== nativeResumeSeq) { console.warn("[PettoBook resume] cancelled after warm-socket (superseded)"); return; } }
              warm = false;
              const r0 = Date.now();
              try { warm = await nativeAptRefetch(profileRef.current || p, { withPeople: true }); } catch { /* ignore */ }
              // eslint-disable-next-line no-console
              console.warn("[PettoBook resume] apt-refetch took " + (Date.now() - r0) + "ms (warm=" + warm + ")");
              if (mySeq !== nativeResumeSeq) { console.warn("[PettoBook resume] cancelled after refetch (superseded)"); return; }
              if (warm) {
                // eslint-disable-next-line no-console
                console.warn("[PettoBook resume] WARM after " + since() + " (attempt " + (i + 1) + ") — appointments are current");
                break;
              }
              // eslint-disable-next-line no-console
              console.warn("[PettoBook] native resume: warm attempt " + (i + 1) + "/" + pauses.length + " failed" + (i < pauses.length - 1 ? " — warming a fresh socket and retrying" : " — leaving recovery to the 45s poll + heartbeat"));
            }
            if (!warm) {
              // eslint-disable-next-line no-console
              console.warn("[PettoBook resume] NOT warm after " + since() + " — falling back to the 45s poll");
              return;
            }
            // Connection proven warm — replay any outboxed (unconfirmed) rows
            // BEFORE the refetch so a recovered appointment appears in this
            // same resume cycle. Existence-checked inside (never overwrites).
            try {
              const { outboxCount, replayOutbox } = await import("./outbox.js");
              if (outboxCount()) {
                const r = await replayOutbox();
                if (r.replayed > 0) {
                  try { if (typeof window.__petosToast === "function") window.__petosToast("已補存先前未儲存的資料 ✓ (" + r.replayed + " 項)", "success"); } catch { /* ignore */ }
                }
              }
            } catch { /* replay retries on next boot */ }
            try { scheduleReload(profileRef.current || p); } catch { /* ignore */ }
            try {
              // TIME-CAPPED. refreshSession() has no timeout of its own, and a
              // hang here used to swallow everything after it — the realtime
              // reconnect never ran, so a resumed app could refetch once and
              // then sit with a dead socket receiving no live updates at all.
              // Measured on Android: this block never completed. A stale token
              // is not worth blocking the reconnect for; the heartbeat and the
              // next refresh will catch it.
              const t1 = Date.now();
              const r = await Promise.race([
                ensureFreshSession(),
                new Promise((resolve) => setTimeout(() => resolve(null), 4000)),
              ]);
              // eslint-disable-next-line no-console
              console.warn("[PettoBook resume] token refresh " + (Date.now() - t1) + "ms (ok=" + !!r?.ok + ")");
              const token = r?.ok ? r.session?.access_token : null;
              if (token) { const { supabase } = await import("./supabaseClient.js"); supabase?.realtime?.setAuth?.(token); }
            } catch { /* ignore */ }
            try { unsubRealtime?.reconnect?.(); } catch { /* ignore */ }
            // eslint-disable-next-line no-console
            console.warn("[PettoBook resume] fully synced in " + since() + " (full refetch + realtime re-armed)");
            // First attempt at 250ms (not 2000ms): the owner expects new
            // bookings/notifications VISIBLE at reopen. The anti-wedge
            // protection was never the delay itself but the ORDER (light warm
            // refetch first, full burst + realtime reconnect only once the
            // socket is proven warm) — that order is unchanged.
          })();
        }
        // PROBE THE DATA PATH (not auth) and reload if it's wedged. This is the
        // fix for "reopen → cannot save/update, need refresh or kill": after a
        // background/wake the HTTP/2 connection to Supabase is often half-dead,
        // and an auth refresh (ensureFreshSession) still SUCCEEDS on a fresh
        // socket while every .from()/write HANGS. The old auth-only probe saw
        // r.ok and returned → app left wedged. unsubRealtime.ping() is a REAL
        // REST read (platform_config) with its own 5s timeout; a failed read
        // means the data connection is genuinely dead and only a reload
        // recovers it. Drafts persist to localStorage (lossless); never mid-form.
        (async () => {
          // Keep the session fresh in the background — ONLY a definitively dead
          // refresh token is a logout reason, never a wedged connection.
          ensureFreshSession().then(r => {
            if (r?.ok) authInvalidStreakRef.current = 0;
            else if (r?.authInvalid) ejectOnAuthInvalid("resume-refresh-invalid");
          }).catch(() => { /* ignore */ });
          // WEB probes the AUTHENTICATED path (the real "can I save?" test) so a
          // wedge that only kills authed traffic is caught — the old public-table
          // ping stayed green and left the app dead. Native is UNCHANGED (keeps
          // ping()), so iOS behaviour is untouched.
          const alive = isNativeResume
            ? (unsubRealtime?.ping ? await unsubRealtime.ping(profileRef.current?.id) : true)
            : (unsubRealtime?.pingAuthed ? await unsubRealtime.pingAuthed(p.id) : (unsubRealtime?.ping ? await unsubRealtime.ping() : true));
          if (alive || editing()) return;            // authed path works (or mid-form) — the refetch above handled freshness
          // NATIVE: NEVER hard-reload on resume. It wedges the WKWebView request
          // queue and — the real bug the owner hit — wipes unsaved in-progress
          // work (a half-finished before/after photo upload, a grooming report,
          // a status change) because editing() only covers the walk-in/bulk
          // sheets. The warm-first branch above already did soft recovery
          // (light refetch + realtime reconnect); live data also self-heals via
          // the 45s native appointment poll + heartbeat reconnect. A genuinely
          // dead connection is recovered by a force-quit (the owner's stated
          // preference) — never by an auto-reload that eats their input.
          if (isNativeResume) return;
          try { flushPersist(); } catch { /* ignore */ }
          // eslint-disable-next-line no-console
          console.warn("[PettoBook] resume: data connection wedged (read probe failed) — reloading to recover");
          if (typeof window !== "undefined") window.location.reload();
        })();
      }
      // Short hidden window: aggressive ref reset to escape the "dead
      // app" state where inflightRef / pendingPersistRef / persistTimerRef
      // got stuck because iOS killed their underlying timers / fetches
      // during the freeze. scheduleReload was looping forever waiting
      // for these to clear, blocking the realtime data refresh, so the
      // user saw stale data and edits never propagated.
      try {
        if (hiddenFor > 1500) {
          // eslint-disable-next-line no-console
          console.warn("[PettoBook] resume after", hiddenFor, "ms — resetting persist + inflight refs to unstick");
          // Cancel any debounce timer that may have died.
          if (persistTimerRef.current) {
            clearTimeout(persistTimerRef.current);
            persistTimerRef.current = null;
          }
          // Wipe stuck inflight counter so scheduleReload can proceed.
          inflightRef.current = 0;
          pendingPersistRef.current = false;
          // If there was a pending diff that never landed, surface it
          // now via flushPersist so the user's last edit isn't lost.
          // flushPersist is a no-op when there's nothing pending.
          try { flushPersist(); } catch { /* ignore */ }
          setSyncStatus({ state: "idle", lastSavedAt: null, error: null });
        }
      } catch { /* ignore */ }
    };
    const onFocus = () => {
      // Window focus doesn't always mean we were hidden — e.g. clicking
      // back into the tab from devtools. Use the same logic as
      // visibilitychange so we don't trigger duplicate reloads.
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      onVisibilityChange();
    };
    // bfcache restore (frozen tab / installed PWA reopened) fires pageshow with
    // persisted=true and OFTEN NOT visibilitychange — the main reason the web app
    // didn't auto-recover on reopen the way the iOS app does. Route it into the
    // same resume recovery.
    const onPageShow = (e) => { if (e && e.persisted) onFocus(); };
    // Stamp the hidden time on pagehide too — the bfcache path can skip the
    // visibilitychange→hidden branch, so this guarantees the resume handler can
    // still tell how long we were away.
    const onPageHide = () => {
      try { if (!sessionStorage.getItem("petos:hiddenAt")) sessionStorage.setItem("petos:hiddenAt", String(Date.now())); } catch { /* ignore */ }
      try { flushPersist(); } catch { /* ignore */ }
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibilityChange);
      window.addEventListener("focus", onFocus);
      window.addEventListener("pageshow", onPageShow);
      window.addEventListener("pagehide", onPageHide);
      // Page Lifecycle API (Chrome/Android memory-saver): a frozen tab restored
      // fires `resume` on document, again possibly without visibilitychange.
      document.addEventListener("resume", onFocus);
    }
    // Capacitor (iOS/Android) — the native bridge stashes a callback on
    // window so we can react to App.addListener("appStateChange") without
    // importing the plugin here. background = treat like "hidden",
    // active = treat like "visible".
    //
    // Phase 2.1: on background, proactively refresh the session if the
    // access_token expires within 30 min. The 50-min interval in
    // supabaseClient.js doesn't fire while iOS has the WebView frozen,
    // so a 24-hour background could wake up with an already-dead token
    // and trigger an immediate forced re-login. Refreshing here means
    // the wake-up token is fresh and the user just stays logged in.
    //
    // Phase 2.2: also stamp the foreground-leave timestamp into
    // Preferences (via localStorage on web) so we can detect long
    // backgrounding (>2h) on resume. The visibility resume already
    // refreshes when hidden > 15s; for native we mirror that and add
    // the long-bg branch that explicitly checks getSession() — if iOS
    // evicted the session from Preferences under memory pressure, the
    // contextual logout fires cleanly instead of the user discovering
    // it via a failed save.
    const onNativeAppState = (state) => {
      if (state === "background") {
        try { flushPersist(); } catch { /* ignore */ }
        hiddenSinceTs = Date.now();
        try { localStorage.setItem("petos:last-foreground-at", String(Date.now())); } catch { /* ignore */ }
        // Proactive refresh: if token expires within 30 min, refresh now.
        (async () => {
          try {
            const { data } = await supabase.auth.getSession();
            const session = data?.session;
            if (!session) return;
            const expMs = (session.expires_at || 0) * 1000;
            if (!expMs) return;
            const msLeft = expMs - Date.now();
            if (msLeft > 0 && msLeft < 30 * 60 * 1000) {
              await ensureFreshSession().catch(() => { /* best effort */ });
            }
          } catch { /* ignore — best-effort */ }
        })();
      } else if (state === "active") {
        // Long-backgrounding detector (Phase 2.2): if it's been > 2 hours
        // since the last foreground stamp, verify session is still alive
        // server-side before letting the user resume. Catches the iOS
        // Preferences-eviction edge case explicitly.
        let lastForeground = 0;
        try { lastForeground = Number(localStorage.getItem("petos:last-foreground-at")) || 0; } catch { /* ignore */ }
        const awayMs = lastForeground ? Date.now() - lastForeground : 0;
        if (awayMs > 2 * 60 * 60 * 1000 && profileRef.current) {
          // Long sleep (overnight etc.): the access token is long expired but
          // the refresh token is valid for weeks. Refresh it — and ONLY log out
          // if that refresh token is truly dead. A transient/network failure
          // must NOT eject the user (that was the "I keep getting logged out"
          // bug); the next attempt / the sessionPoll will recover.
          (async () => {
            const r = await ensureFreshSession();
            if (r.ok) authInvalidStreakRef.current = 0;
            else if (r.authInvalid) {
              await ejectOnAuthInvalid("long-bg-refresh-invalid");
              try { if (typeof window !== "undefined" && typeof window.__petosToast === "function") window.__petosToast("Session 已過期，請重新登入", "info"); } catch { /* ignore */ }
            }
            // ok or transient → keep the user logged in.
          })();
        }
        // Force-visible: Capacitor says the app IS active; don't let a stale
        // document.visibilityState ("hidden" for a beat on iOS reopen) turn
        // this resume into a phantom hide. See onVisibilityChange.
        onVisibilityChange(true);
      }
    };
    if (typeof window !== "undefined") {
      window.__petosOnAppState = onNativeAppState;
    }

    // Realtime heartbeat: while the tab is visible, ping every 30s. If the
    // ping times out at 5s, force-reconnect. This catches zombie sockets
    // before the user notices stale data.
    // Consecutive ping failures. A single miss can be a transient blip; TWO
    // in a row (~60s) means the connection is genuinely wedged — this is the
    // "web/PWA goes dead after a few hours, needs manual reload" case: a stale
    // HTTP/2 pool / zombie supabase client that reconnect+refetch can't cure,
    // and which the resume-path reload never reaches because the tab was never
    // hidden. So the foreground heartbeat itself must escalate to a reload.
    let heartbeatFails = 0;
    const RECOVERY_RELOAD_COOLDOWN_MS = 5 * 60 * 1000; // cap recovery reloads → never loops
    const heartbeat = setInterval(async () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      if (!profileRef.current || !unsubRealtime?.ping) return;
      // WEB probes the AUTHENTICATED path (the public-table ping stayed green on
      // a wedged authed connection, so a foreground wedge was never caught here
      // and never escalated to the reload below). Native UNCHANGED.
      const isNativeHb = typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();
      const ok = (!isNativeHb && unsubRealtime.pingAuthed)
        ? await unsubRealtime.pingAuthed(profileRef.current.id)
        : await unsubRealtime.ping(profileRef.current?.id);
      if (ok) { heartbeatFails = 0; return; }
      heartbeatFails++;
      // eslint-disable-next-line no-console
      console.warn("[PettoBook] heartbeat failed x" + heartbeatFails + " — reconnecting realtime");
      try { unsubRealtime.reconnect?.(); } catch { /* ignore */ }
      scheduleReload(profileRef.current);
      // A dead token can also present as a dead connection — refresh once
      // (serialized + transient-safe; only ejects on a definitively dead token).
      try {
        const r = await ensureFreshSession();
        if (r?.ok) authInvalidStreakRef.current = 0;
        else if (r?.authInvalid) { await ejectOnAuthInvalid("heartbeat-refresh-invalid"); return; }
      } catch { /* ignore */ }
      // Escalation: connection confirmed dead across ≥2 probes. WEB/PWA only
      // (native WKWebView wedges on reload storms — it keeps the lighter
      // reconnect+refetch path), when the browser is online and the user isn't
      // mid-form. Safe because index.html is no-store + the SW is
      // network-passthrough, so a reload boots fresh = the automated manual
      // refresh. Cooldown (sessionStorage, survives the reload) caps it to once
      // per 5 min so a server outage can't cause a reload loop.
      const isNative = typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();
      const editing = typeof window !== "undefined" && window.__petosEditingForm === true;
      const online = typeof navigator === "undefined" || navigator.onLine !== false;
      if (heartbeatFails >= 2 && !isNative && online && !editing) {
        let lastReload = 0;
        try { lastReload = Number(sessionStorage.getItem("petos:last-recovery-reload")) || 0; } catch { /* ignore */ }
        if (Date.now() - lastReload > RECOVERY_RELOAD_COOLDOWN_MS) {
          // eslint-disable-next-line no-console
          console.warn("[PettoBook] connection dead — reloading to recover");
          try { sessionStorage.setItem("petos:last-recovery-reload", String(Date.now())); } catch { /* ignore */ }
          heartbeatFails = 0;
          try { window.location.reload(); } catch { /* ignore */ }
        }
      }
    }, 30000);

    // WEB foreground data poll — the guaranteed-fresh floor under realtime.
    // Realtime (postgres_changes) is the INSTANT path, but a browser websocket
    // can go "zombie": still connected yet silently no longer delivering (after
    // a network blip / laptop sleep / proxy idle-timeout). The REST-based
    // heartbeat above can't detect that (REST still works), and there's no
    // other fallback — so the web app showed stale data (new / edited
    // appointments made elsewhere) until a manual refresh. Poll the SAME safe
    // path realtime uses — scheduleReload → silent, write-drain-guarded
    // reload(p) that never wipes an in-progress form — every 30s while visible.
    // When realtime IS delivering, scheduleReload's 4s throttle coalesces these
    // so it's nearly free; when it's dead, the app self-heals within 30s.
    // NATIVE is EXCLUDED: on the fragile iOS WKWebView a sustained full-reload
    // storm saturates the request queue and wedges the app (that's why the old
    // native 60s poll was reverted) — native relies on realtime + the resume
    // catch-up refetch in onVisibilityChange instead.
    const webDataPoll = setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      if (!profileRef.current) return;
      const isNative = typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();
      if (isNative) return;
      scheduleReload(profileRef.current);
    }, 30000);

    // NATIVE foreground appointment sync. On the iOS/Android WKWebView the
    // realtime WebSocket goes "zombie" (still joined, silently delivering
    // nothing) and/or its token goes stale after rotation, so appointment edits
    // from other clients never arrive — even though HTTP saves work fine (they
    // carry a fresh token). The old fix (a full scheduleReload poll) wedged the
    // WKWebView request queue and was reverted. This is the LIGHT replacement:
    // ONE scoped appointments query every 45s (owner/staff → tenant, customer →
    // own), merged into state — negligible load, no full-state burst. Guarded so
    // it can't clobber an optimistic in-flight write, and skipped for superadmin
    // (multi-tenant is heavier — that view refreshes on resume). WEB is excluded
    // (it has the webDataPoll above).
    const nativeAptPoll = setInterval(() => {
      const isNative = typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();
      if (!isNative) return;
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      nativeAptRefetch(profileRef.current); // shared light refetch (write-drain guarded inside)
    }, 45000);

    return () => {
      clearInterval(safety);
      clearInterval(heartbeat);
      clearInterval(webDataPoll);
      clearInterval(nativeAptPoll);
      clearInterval(sessionPoll);
      if (typeof window !== "undefined") {
        window.removeEventListener("petos:session-expired", onSessionExpired);
      }
      if (oauthWatchdog) clearTimeout(oauthWatchdog);
      if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
      if (resumeWatchdogRef.current) clearTimeout(resumeWatchdogRef.current);
      unsubAuth();
      unsubRealtime();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibilityChange);
        window.removeEventListener("focus", onFocus);
        window.removeEventListener("pageshow", onPageShow);
        window.removeEventListener("pagehide", onPageHide);
        document.removeEventListener("resume", onFocus);
      }
      if (typeof window !== "undefined" && window.__petosOnAppState === onNativeAppState) {
        delete window.__petosOnAppState;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doSignIn = useCallback(async (email, password) => {
    // Flip loading=true synchronously so the App's outer loader takes over
    // immediately. Without this there's a brief window between
    // signInWithPassword resolving and onAuthChange firing where the parent
    // would render the Login/MarketingHome screen, producing a visible
    // "landing page flash" before the role view arrives.
    setLoading(true);
    setError(null);
    try {
      return await signIn({ email, password });
    } catch (e) {
      setLoading(false);
      throw e;
    }
    // Note: on success we DON'T flip loading=false here; onAuthChange takes
    // over and unsets it once the post-login reload completes.
  }, []);

  const doSignOut = useCallback(async () => {
    // Clear browser-scoped UI flags so the NEXT user doesn't inherit them.
    // Especially important for "petos:view-as": a superadmin who flipped to
    // the platform console would otherwise leave that preference behind for
    // any other is_super user that shares the browser (privilege escalation).
    // Also wipe signup / pending-promo / oauth markers — user A signing up
    // via /shop/foo then bouncing out before email-confirm left B (same
    // browser) silently joined to A's tenant via the membership-ensure
    // post-login block.
    try {
      const lastId = localStorage.getItem("petos:lastUserId");
      if (lastId) clearCachedState(lastId);
      // Sweep every key under our `petos:` namespace except the
      // intentionally-persisted theme / language preferences.
      const keepKeys = new Set(["petos:theme", "petos:locale", "petos:schedule-zoom", "petos:report-order"]);
      const toDelete = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith("petos:") && !keepKeys.has(k)) toDelete.push(k);
      }
      toDelete.forEach(k => localStorage.removeItem(k));
      sessionStorage.removeItem("petos:claim-summary");
    } catch { /* localStorage unavailable — ignore */ }
    // Run server signOut, but ALWAYS force-clear local profile state. A
    // stale/expired access token used to cause authSignOut to throw on
    // POST /logout — and because the local profile was never reset, the
    // UI stayed on the role view with every action 401-ing. Resilient
    // signOut now never throws, but we still clear state here as a
    // belt-and-braces guarantee.
    // Never let a hung sign-out keep the user on screen: give it 4 s.
    try { await Promise.race([authSignOut(), new Promise(r => setTimeout(r, 4000))]); }
    catch (e) { console.warn("[PettoBook] signOut error (clearing local anyway):", e?.message || e); }
    lastBootedUserIdRef.current = null;
    profileRef.current = null;
    setProfile(null);
    setStateRaw(EMPTY);
    setError(null);
    setLoading(false);
  }, []);

  // Re-bind the realtime channel to a different tenant id. Used when a
  // customer switches active shop — without this, postgres_changes still
  // filters on their PRIMARY tenant_id and updates to the actively-viewed
  // shop never arrive until a full page reload. Pass null to restore the
  // profile's default tenant filter.
  const setRealtimeTenant = useCallback((tenantIdOverride) => {
    try {
      const baseProfile = profileRef.current;
      if (!baseProfile) return;
      const nextProfile = tenantIdOverride
        ? { ...baseProfile, tenantId: tenantIdOverride }
        : baseProfile;
      const current = realtimeRef.current;
      // If we're already subscribed to this tenant, no-op.
      if (current?.profile?.tenantId === nextProfile.tenantId) return;
      try { current?.unsub?.(); } catch { /* ignore */ }
      const nextUnsub = subscribeChanges({
        profile: nextProfile,
        onChange: () => scheduleReload(baseProfile),
      });
      realtimeRef.current = { unsub: nextUnsub, profile: nextProfile };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[PettoBook] setRealtimeTenant failed:", e);
    }
  }, [scheduleReload]);

  return { state, setState, profile, loading, error, syncStatus, signIn: doSignIn, signOut: doSignOut, reload, setRealtimeTenant, supabase };
}
